/**
 * TicketDetailAgentPage - the agent's full-page view of a ticket
 * (SPEC §9).
 *
 * Layout (top to bottom, May 2026 rev 2):
 *   - Breadcrumb (tiny) back to /tickets.
 *   - Prominent header: H1 subject + StatusBadge + PriorityBadge on
 *     line 1; TICKET-# / requester / company on line 2; action bar
 *     (Reply, Forward, Close, Mark spam, Delete) on the right.
 *   - Composer card. Collapsed by default (single-line "Type your
 *     reply" affordance). Expands on click, when an action button
 *     in the header triggers it, or when a draft already exists.
 *   - Conversation thread - newest reply at the top, the original
 *     ticket rendered as the bottom-most "Original ticket" entry.
 *     Bubbles are color-coded per SPEC §9 (public reply blue,
 *     customer neutral, internal note amber, API-key draft violet).
 *   - Right rail: big status header, then Properties card, then
 *     audit timeline (last 20 events), then related-tickets rail.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Alert, Button, Card, Modal, Select, Spinner } from "@/components/ui";
import { StatusBadge } from "@/components/tickets/StatusBadge";
import { PriorityBadge } from "@/components/tickets/PriorityBadge";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  useAddAgentComment,
  useAgentTicketComments,
  useDeleteAgentComment,
  useEditAgentComment,
  useSendDraftComment,
} from "@/hooks/useAgentTicketComments";
import {
  agentTicketAttachmentsQueryKey,
  fetchAgentAttachmentDownloadUrl,
  useAgentTicketAttachments,
  useAgentTicketDetail,
  useDeleteAgentAttachment,
  useAttachExistingToDraft,
  useUploadAgentAttachment,
} from "@/hooks/useAgentTicketDetail";
import { useSoftDeleteTicket } from "@/hooks/useAgentDeleteTicket";
import { useMarkSpam } from "@/hooks/useAgentMarkSpam";
import {
  firstNameOf,
  renderReplySignatureHtml,
  useReplySignature,
} from "@/hooks/useReplySignature";
import { useTicketEvents } from "@/hooks/useTicketEvents";
import {
  useAddWatcher,
  useRemoveWatcher,
  useTicketWatchers,
} from "@/hooks/useTicketWatchers";
import { useUpdateTicket } from "@/hooks/useUpdateTicket";
import { safeHtmlProps } from "@/lib/sanitize";
import api from "@/lib/api";
import { computeSeedTo } from "@/lib/replySeed";
import { commentEditorSeedHtml } from "@/lib/commentEditorSeed";
import { useAuthStore } from "@/stores/auth";
import type {
  AttachmentResponse,
  CommentResponse,
  CommentUpdateRequest,
  TicketPriority,
  TicketStatus,
  TicketType,
} from "@/types/api";

import { AgentNotifyPicker } from "@/components/tickets/AgentNotifyPicker";
import { AssigneePicker } from "@/components/tickets/AssigneePicker";
import { MergeTicketModal } from "@/components/tickets/MergeTicketModal";
import { SplitTicketModal } from "@/components/tickets/SplitTicketModal";
import { MoveCommentModal } from "@/components/tickets/MoveCommentModal";
import { TicketOwnerPicker } from "@/components/tickets/TicketOwnerPicker";
import { AttachmentDropzone } from "@/components/tickets/AttachmentDropzone";
import {
  AuditTimeline,
  focusCommentAnchor,
} from "@/components/tickets/AuditTimeline";
import { CurrentStatusCard } from "@/components/tickets/CurrentStatusCard";
import { EditableTicketSubject } from "@/components/tickets/EditableTicketSubject";
import { DocumentsCard } from "@/components/tickets/DocumentsCard";
import { RecentlyViewedTicketsRail } from "@/components/tickets/RecentlyViewedTicketsRail";
import { RecipientHeader } from "@/components/tickets/RecipientHeader";
import { RichTextEditor } from "@/components/tickets/RichTextEditor";
import { TagsEditor } from "@/components/tickets/TagsEditor";
import { ReminderControl } from "@/components/tickets/ReminderControl";

type ComposerTab = "public" | "internal";

// What Save Draft adds to the PATCH on an AI draft, beyond the body.
type DraftSaveFields = Pick<
  CommentUpdateRequest,
  "subject" | "to_emails" | "cc_emails" | "bcc_emails"
>;

/**
 * How many of the newest messages arrive with their bodies loaded.
 *
 * A worked email thread quotes itself, so its comment bodies grow
 * without bound: prod ticket HK4 carries 1.98 MB of HTML across 66
 * comments, all of which the page used to download every 15 seconds and
 * re-sanitize on every keystroke. Six is Greg's number and it matches
 * how the page is actually used - the last customer message plus a few
 * turns of context. Everything older is one click away via
 * "Load Remaining History"; its content is not downloaded, not
 * sanitized, and not in the DOM until then.
 *
 * See plans/2026-08-24-ticket-detail-comment-windowing.md.
 */
const DEFAULT_BODY_WINDOW = 6;

function composerTabKey(ticketNumber: number): string {
  return `buyist-support:composer-tab:${ticketNumber}`;
}

function readComposerTab(ticketNumber: number): ComposerTab {
  try {
    const v = localStorage.getItem(composerTabKey(ticketNumber));
    if (v === "internal" || v === "public") return v;
  } catch {
    /* noop */
  }
  return "public";
}

function writeComposerTab(ticketNumber: number, tab: ComposerTab): void {
  try {
    localStorage.setItem(composerTabKey(ticketNumber), tab);
  } catch {
    /* noop */
  }
}

const STATUS_OPTIONS: { value: TicketStatus; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "pending", label: "Pending" },
  { value: "waiting_customer", label: "Waiting on Customer" },
  { value: "waiting_third_party", label: "Waiting on Third Party" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
  { value: "reopened", label: "Reopened" },
];

const PRIORITY_OPTIONS: { value: TicketPriority; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

// Real types sorted alphabetically; the "(no type)" sentinel stays first.
const TYPE_OPTIONS: { value: "" | TicketType; label: string }[] = [
  { value: "", label: "(no type)" },
  { value: "DevOps Task", label: "DevOps Task" },
  { value: "Failed Credit Card Charge", label: "Failed Credit Card Charge" },
  { value: "Internal Task", label: "Internal Task" },
  { value: "Platform Development", label: "Platform Development" },
  { value: "Problem", label: "Problem" },
  { value: "Question", label: "Question" },
  { value: "Service Request", label: "Service Request" },
  { value: "Vendor Support", label: "Vendor Support" },
];

function fmtTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Year included so comments on multi-year-old imported tickets
  // (Freshdesk / Gemini archive) are unambiguous.
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Date-only with the year, for the header meta row. */
function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Per-tile hover affordances that mirror FreshDesk's attachment chips:
 * Download, Attach to Response, Remove.
 *
 * - Download fetches a signed Spaces URL and triggers a download.
 * - Attach to Response stages the attachment id on the active reply
 *   draft; the backend clones the row on send (shared Spaces bytes,
 *   new DB row scoped to the new comment).
 * - Remove opens a confirmation modal owned by the parent page and,
 *   on confirm, hard-deletes the attachment row.
 *
 * The Attach and Remove handlers come in as optional props: a
 * customer-side or read-only render simply omits them and the icons
 * disappear (no action = no button). ``isAttachedToDraft`` flips the
 * Attach icon to a "staged" state so the agent can see at a glance
 * which files are queued onto the next reply.
 */
function AttachmentTile({
  attachment,
  ticketNumber,
  onAttachToResponse,
  onRemove,
  isAttachedToDraft,
}: {
  attachment: AttachmentResponse;
  ticketNumber: number;
  onAttachToResponse?: (attachment: AttachmentResponse) => void;
  onRemove?: (attachment: AttachmentResponse) => void;
  isAttachedToDraft?: boolean;
}): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Anchor clicks don't carry the JWT Bearer token (the axios
  // interceptor adds it to programmatic requests only). So we fetch
  // the signed URL via axios first, then open it - the signed URL
  // carries its own auth via query-string signature and points
  // straight at DO Spaces. The image branch shares this flow because
  // <img src> has the same problem an <a href> does.
  async function handleDownload(): Promise<void> {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      const url = await fetchAgentAttachmentDownloadUrl(
        ticketNumber,
        attachment.id,
      );
      // Programmatic anchor click is more reliable than window.open
      // here: popup blockers leave it alone (the browser treats it as
      // a download initiation), and DO Spaces returns
      // Content-Disposition: attachment so the file downloads instead
      // of opening as a tab. target="_blank" + rel="noopener" stays
      // for the cases where the browser does open a tab (e.g. an
      // unknown content-type that the browser can render inline).
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: unknown) {
      // Surface the 503 from missing Spaces credentials (and any
      // 404 / 401 / 500) inline rather than silently doing nothing.
      const message =
        e instanceof Error
          ? e.message
          : "Could not get a download URL for this attachment.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }

  const isImage = attachment.content_type.startsWith("image/");
  return (
    <div
      className="group relative flex flex-col gap-1"
      data-testid="attachment-tile"
    >
      <div className="flex items-center gap-3 rounded border border-neutral-200 bg-white px-3 py-2 pr-20 text-sm transition group-hover:bg-neutral-50">
        <i
          className={`fa-duotone fa-thin ${
            isImage ? "fa-image" : "fa-file"
          } text-2xl text-neutral-500`}
          aria-hidden="true"
        />
        <div className="min-w-0 text-left">
          <div className="truncate text-sm text-neutral-800">
            {attachment.filename}
          </div>
          <div className="text-xs text-neutral-500">
            {formatBytes(attachment.byte_size)}
            {isLoading && <span className="ml-2">opening...</span>}
          </div>
        </div>
        {/* Hover action cluster - mirrors FreshDesk's three-icon row.
            Always rendered (so it doesn't shift layout on hover), but
            opacity-0 until the tile is hovered or focused. */}
        <div className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 opacity-0 transition group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
          <button
            type="button"
            onClick={() => {
              void handleDownload();
            }}
            disabled={isLoading}
            title="Download"
            aria-label={`Download ${attachment.filename}`}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800 disabled:cursor-progress disabled:opacity-60"
          >
            <i
              className="fa-duotone fa-thin fa-download text-sm"
              aria-hidden="true"
            />
          </button>
          {onAttachToResponse && (
            <button
              type="button"
              onClick={() => onAttachToResponse(attachment)}
              title={
                isAttachedToDraft
                  ? "Already attached to your reply"
                  : "Attach to Response"
              }
              aria-label={
                isAttachedToDraft
                  ? `Already attached to your reply: ${attachment.filename}`
                  : `Attach ${attachment.filename} to your reply`
              }
              aria-pressed={isAttachedToDraft ? true : false}
              className={`rounded p-1 ${
                isAttachedToDraft
                  ? "text-blue-600 hover:bg-blue-100"
                  : "text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800"
              }`}
            >
              <i
                className={`fa-duotone fa-thin ${
                  isAttachedToDraft ? "fa-paperclip-vertical" : "fa-paperclip"
                } text-sm`}
                aria-hidden="true"
              />
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(attachment)}
              title="Remove attachment"
              aria-label={`Remove ${attachment.filename}`}
              className="rounded p-1 text-neutral-500 hover:bg-red-100 hover:text-red-700"
            >
              <i
                className="fa-duotone fa-thin fa-trash text-sm"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>
      {error && (
        <div className="text-xs text-red-600" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * AgentCommentBubble - color-coded per SPEC §9.
 *
 *   author_kind=customer            -> bg-neutral-50  / customer name
 *   author_kind=agent, !is_private  -> bg-blue-50     / agent name + "Public reply"
 *   author_kind=agent, is_private   -> bg-amber-50    / agent name + "Internal note"
 *   author_kind=api_key, is_private -> bg-violet-50   / "Draft from API key"
 *                                      when unsent-draft, otherwise
 *                                      "Internal note (API)"
 *   author_kind=system              -> small italic line, no bubble
 *
 * Edit / delete affordances render in the top-right of the bubble only
 * for internal notes the current agent authored (system_admins can
 * mutate anyone's note). Public replies are not editable because the
 * outbound email already shipped.
 */
function AgentCommentBubble({
  comment,
  attachments,
  ticketNumber,
  ticketPublicCode,
  ticketSubject,
  currentUserId,
  isSystemAdmin,
  requesterEmail,
  requesterName,
  fromEmail,
  fromName,
  onEdit,
  onDelete,
  isEditing,
  isSaving,
  onSaveEdit,
  onCancelEdit,
  onSendDraft,
  isSendingDraft,
  isDeletingThis,
  onAttachToResponse,
  onRemoveAttachment,
  onUploadDraftFiles,
  onUploadInlineImage,
  attachedToDraftIds,
  defaultCollapsedMobile = false,
  onSplitFrom,
  onMove,
}: {
  comment: CommentResponse;
  // Opens the split dialog starting at this customer email. Omitted
  // where splitting is not allowed (spam / deleted / merged / archive).
  onSplitFrom?: (commentId: string) => void;
  // Opens the move dialog for this message (any kind but system rows).
  onMove?: (comment: CommentResponse) => void;
  // Files attached to THIS comment (filtered by comment_id upstream).
  // When the comment is "system" we never render attachments inline -
  // those are status flips, not user content.
  attachments: AttachmentResponse[];
  ticketNumber: number;
  // Parent ticket's public code + current subject. Used by the
  // AI-draft path to seed the subject input when ``comment.subject``
  // is null (the AI never wrote one). The bubble itself doesn't
  // re-fetch the ticket; the parent passes these in.
  ticketPublicCode: string;
  ticketSubject: string;
  currentUserId: string | null;
  isSystemAdmin: boolean;
  // The AI-draft RecipientHeader's To default. Cc / Bcc come off the
  // draft row itself (see initialCc below).
  requesterEmail?: string;
  requesterName?: string;
  fromEmail?: string;
  fromName?: string;
  onEdit: (commentId: string) => void;
  onDelete: (commentId: string) => void;
  isEditing: boolean;
  isSaving: boolean;
  // commentId is passed by the bubble itself so the same callback can
  // serve both the parent-tracked Edit flow (internal-note hover-Edit)
  // and the auto-edit AI-draft flow (no parent editingCommentId state).
  // ``draftFields`` is Save Draft's To / Cc / Bcc / subject; the
  // internal-note Edit flow has none and omits it.
  onSaveEdit: (
    html: string,
    text: string,
    commentId: string,
    draftFields?: DraftSaveFields,
  ) => Promise<void> | void;
  onCancelEdit: () => void;
  // Promote an API-key draft into a public reply (sends the email).
  // Omitted on read-only call sites; when missing, the Send Reply
  // button is suppressed (Save Draft still works via onSaveEdit).
  //
  // ``recipients`` is OPTIONAL: when the bubble's recipient header is
  // mounted (AI-draft path) it passes the agent's current to/cc/bcc
  // selections through so the backend can update the draft row before
  // the outbound fires. The internal-note Edit flow doesn't render
  // the header so it sends undefined and the backend preserves the
  // existing recipients.
  onSendDraft?: (
    commentId: string,
    html: string,
    text: string,
    recipients?: {
      to_emails: string[];
      cc_emails: string[];
      bcc_emails: string[];
    },
    // Cleaned subject the agent typed in the API-Reply bubble. Empty
    // / undefined falls back to the deterministic
    // ``[#{public_code}] {ticket.subject}`` shape server-side; non-
    // empty wins (with the ``[#code]`` token enforced at send time).
    subject?: string,
  ) => Promise<void> | void;
  isSendingDraft?: boolean;
  isDeletingThis: boolean;
  // Optional - the bubble passes them straight through to each
  // AttachmentTile. Customer surface / read-only paths omit them and
  // the hover icons just don't render.
  onAttachToResponse?: (attachment: AttachmentResponse) => void;
  onRemoveAttachment?: (attachment: AttachmentResponse) => void;
  // Upload files onto an unsent AI draft. The draft comment row already
  // exists, so each file links to it directly (comment_id on the upload)
  // and notification_service._load_attachments picks it up when Send
  // Reply mails the draft out. Resolves to the list of per-file error
  // strings; empty means all ok.
  onUploadDraftFiles?: (commentId: string, files: File[]) => Promise<string[]>;
  // Pasted / dropped screenshots in an open AI draft. Same inline-image
  // upload the main composer uses; the backend binds the images to the
  // draft row on Save Draft and Send Reply.
  onUploadInlineImage?: (file: File) => Promise<string>;
  attachedToDraftIds?: ReadonlySet<string>;
  // Mobile-only: when true, this bubble's body starts collapsed behind
  // a one-line header on small screens (< lg). Desktop shows the body
  // regardless (that collapse is gated entirely on `lg:` classes).
  // The parent sets this true for every message EXCEPT the most-recent
  // customer reply, so an agent on a phone sees the latest customer
  // message immediately and taps to open the rest. Editors (AI drafts /
  // active edit) ignore this and stay open - see `showEditor` below.
  // API-authored internal notes collapse at every width instead; that
  // rule is decided inside the bubble, not here.
  defaultCollapsedMobile?: boolean;
}): JSX.Element {
  // Unsent API-key drafts are the "Claude wrote a reply, agent
  // reviews + sends" path. They auto-open in edit mode so the agent
  // does not have to click into the bubble before they can ship it -
  // per Greg's "we'll always need to edit it" note. is_private goes
  // false the moment the send succeeds, so this flag flips to false
  // immediately on success and the bubble collapses to its public-
  // reply render.
  // ``is_draft`` distinguishes an unsent AI public-reply DRAFT from a
  // terminal AI internal note (both are api_key + private + not sent).
  // Without it, an AI internal note would wrongly render as a draft with
  // a "send as public reply" button. Backfilled true for pre-existing
  // drafts by migration 20260607_0024.
  const isAiDraft =
    comment.author_kind === "api_key" &&
    comment.is_private &&
    !comment.sent_via_email &&
    comment.is_draft;
  const showEditor = isEditing || isAiDraft;

  // API-authored internal notes - the AI's session summaries and
  // triage passes, NOT its unsent drafts - start collapsed at every
  // width (Greg, 2026-07-30). A worked ticket accumulates many of
  // them, each one long, and they bury the customer replies and human
  // notes in between. Drafts are excluded because they are pending
  // work an agent has to notice and send; they render as an editor and
  // `showEditor` keeps them open anyway.
  const isApiInternalNote =
    comment.author_kind === "api_key" && comment.is_private && !isAiDraft;

  // Collapse state. `collapseScope` says whether the collapse applies
  // at all widths or only on phones. The mobile-only case has to stay
  // open on desktop with no viewport JS at all, which is what the
  // `lg:` classes below do; the all-widths case just hides the body.
  // An editor view is never collapsible, so when `showEditor` is true
  // the body is open and the toggle does not render.
  const collapseScope: "all" | "mobile" = isApiInternalNote ? "all" : "mobile";
  const [open, setOpen] = useState(
    !(isApiInternalNote || defaultCollapsedMobile),
  );
  const isCollapsible = !showEditor;
  const bodyOpen = showEditor || open;
  // One-line preview under the header of a collapsed bubble, so a
  // stack of collapsed notes is still scannable without opening each.
  const collapsedSnippet = useMemo(() => {
    const flat = (comment.body_text ?? "").replace(/\s+/g, " ").trim();
    return flat.length > 140 ? `${flat.slice(0, 140)}...` : flat;
  }, [comment.body_text]);

  // Sanitizing is the single most expensive thing this page does, and it
  // used to run inline in the JSX below - which meant DOMPurify re-parsed
  // and re-serialized every message body on EVERY render of the page.
  // The composer's draft lives in page state, so "every render" is "every
  // keystroke": measured at ~1.1 s of DOMPurify work per keystroke for
  // prod ticket HK4's 1.98 MB of comment HTML (jsdom; see
  // plans/2026-08-24-ticket-detail-comment-windowing.md section 1.2).
  // The body only changes when the server sends a different one, so the
  // memo key is the raw HTML itself. React compares the ``__html``
  // string before touching the DOM, so a stable object here is purely a
  // CPU saving - the rendered output is unchanged.
  const bodyHtmlProps = useMemo(
    () => safeHtmlProps(comment.body_html ?? ""),
    [comment.body_html],
  );

  // Seeded through commentEditorSeedHtml, not straight off body_html:
  // API-authored notes arrive as body_text only, and seeding from
  // body_html alone opened those blank (Greg, 2026-08-03).
  const [draftHtml, setDraftHtml] = useState<string>(
    commentEditorSeedHtml(comment.body_html, comment.body_text),
  );
  const [draftText, setDraftText] = useState<string>(comment.body_text);
  // Subject draft for AI-Reply bubbles. Pre-seeded from
  // ``comment.subject`` (the AI usually leaves this null; the
  // backend's deterministic fallback would produce
  // ``[#code] ticket.subject``). When null, we seed with the same
  // shape the Public Reply composer uses so the agent sees the
  // structural [#code] token they can clean around.
  const initialSubject =
    comment.subject ?? `[#${ticketPublicCode}] ${ticketSubject}`;
  const [draftSubject, setDraftSubject] = useState<string>(initialSubject);

  // AI-draft recipient state. Pre-seeded once per (comment, showEditor)
  // pair from the draft row, and ONLY the row:
  //   - To: any to_emails on the draft; otherwise the ticket requester.
  //   - Cc / Bcc: the draft's cc_emails / bcc_emails, deduped against
  //     To. The ticket's watchers are already in there when the AI left
  //     a list empty - the backend stores them when the draft is written
  //     (ai_autosend_service.watcher_audience).
  // This used to union the ticket's CC watchers in at mount (Greg,
  // 2026-09-30, WP2). That put back every watcher the AI had
  // deliberately left off, overwrote the Cc Greg had saved, and raced
  // the watchers query: a cold load could mount before watchers arrived
  // and show a shorter list than a warm one.
  // We don't reseed on background refetch for the same reason we don't
  // reseed draftHtml: it would clobber the agent's in-progress edits to
  // the recipient list.
  const initialTo = useMemo<string[]>(() => {
    if (comment.to_emails.length > 0) return [...comment.to_emails];
    return requesterEmail ? [requesterEmail] : [];
  }, [comment.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Self-suppression set for the AI-draft seed. Mirrors the parent's
  // ccCandidates filter so the bubble never echoes our own outbound
  // mailbox into CC or BCC even when the AI itself listed it. Greg
  // had to manually remove it on every reply before this filter.
  const selfAddresses = useMemo<Set<string>>(() => {
    const out = new Set<string>(["support@buyist.com"]);
    if (fromEmail) out.add(fromEmail.toLowerCase());
    return out;
  }, [fromEmail]);
  const initialCc = useMemo<string[]>(() => {
    const seen = new Set<string>(
      (comment.to_emails.length > 0 ? comment.to_emails : requesterEmail ? [requesterEmail] : [])
        .map((e) => e.toLowerCase()),
    );
    const out: string[] = [];
    const push = (email: string): void => {
      const k = email.toLowerCase();
      if (!seen.has(k) && !selfAddresses.has(k)) {
        seen.add(k);
        out.push(email);
      }
    };
    (comment.cc_emails ?? []).forEach(push);
    return out;
  }, [comment.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const initialBcc = useMemo<string[]>(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    (comment.bcc_emails ?? []).forEach((e) => {
      const k = e.toLowerCase();
      if (!seen.has(k) && !selfAddresses.has(k)) {
        seen.add(k);
        out.push(e);
      }
    });
    return out;
  }, [comment.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [toEmails, setToEmails] = useState<string[]>(initialTo);
  const [ccEmails, setCcEmails] = useState<string[]>(initialCc);
  const [bccEmails, setBccEmails] = useState<string[]>(initialBcc);

  // Draft-attachment upload state. Files dropped on an open AI draft
  // upload straight onto the draft comment; the parent writes the new
  // rows into the attachments cache so the strip below the editor
  // shows them in the same render.
  const [attachError, setAttachError] = useState<string | null>(null);
  const [isAttaching, setIsAttaching] = useState(false);

  async function attachDroppedFiles(files: File[]): Promise<void> {
    if (files.length === 0 || !onUploadDraftFiles) return;
    setAttachError(null);
    setIsAttaching(true);
    try {
      const errors = await onUploadDraftFiles(comment.id, files);
      setAttachError(
        errors.length > 0 ? `Some files failed: ${errors.join("; ")}` : null,
      );
    } finally {
      setIsAttaching(false);
    }
  }

  // Re-init local state only when (a) we enter edit mode (parent flips
  // isEditing on), or (b) the bubble re-keys on a different comment id.
  // Deliberately NOT re-init on body_html/body_text changes: background
  // refetches (15s polling) would otherwise clobber the agent's
  // in-progress local edits. The mismatch we'd lose is the rare server-
  // side mutation while the agent is typing, which is a worse trade than
  // a typing session getting wiped every 15 seconds.
  useEffect(() => {
    if (showEditor) {
      setDraftHtml(commentEditorSeedHtml(comment.body_html, comment.body_text));
      setDraftText(comment.body_text);
      setToEmails(initialTo);
      setCcEmails(initialCc);
      setBccEmails(initialBcc);
      setDraftSubject(initialSubject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment.id, showEditor]);

  if (comment.author_kind === "system") {
    return (
      <div className="my-2 text-center text-xs italic text-neutral-500">
        {comment.body_text}
        <span className="ml-2">{fmtTimestamp(comment.created_at)}</span>
      </div>
    );
  }

  let containerClasses = "bg-neutral-50 border-neutral-200";
  let badge: JSX.Element | null = null;

  if (comment.author_kind === "api_key" && comment.is_private) {
    containerClasses = "bg-violet-50 border-violet-200";
    badge = (
      // Both shapes are api_key + private, and they are different
      // things to an agent: a draft is unsent work waiting on a human,
      // a note is the AI's own record. One badge reading "Draft" for
      // both was misleading once the notes started collapsing.
      <span
        data-testid="bubble-badge"
        className="inline-flex items-center rounded-full bg-violet-200 px-2 py-0.5 text-xs font-medium text-violet-800"
      >
        {isAiDraft ? "Draft from API key" : "Internal note (API)"}
      </span>
    );
  } else if (comment.author_kind === "agent" && !comment.is_private) {
    containerClasses = "bg-blue-50 border-blue-200";
    badge = (
      <span
        data-testid="bubble-badge"
        className="inline-flex items-center rounded-full bg-blue-200 px-2 py-0.5 text-xs font-medium text-blue-800"
      >
        Public reply
      </span>
    );
  } else if (comment.is_private) {
    // Any non-customer private comment renders as an internal note.
    containerClasses = "bg-amber-50 border-amber-200";
    badge = (
      <span
        data-testid="bubble-badge"
        className="inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-800"
      >
        Internal note
      </span>
    );
  }

  // Edit / delete are gated to internal notes + (author or admin).
  // Public replies are deliberately NOT editable - the outbound email
  // already landed, so editing the stored body would create a divergence
  // between what the customer received and what the ticket shows. The
  // same goes for deletion.
  //
  // AI drafts (api_key + is_private + not sent) are a special case:
  // they have no human author_user_id, so the "are you the author"
  // check is never true, but any agent on the team is allowed to edit
  // / send / discard them. The backend mirrors this in _can_modify.
  const canMutate =
    comment.is_private &&
    (isAiDraft ||
      isSystemAdmin ||
      (currentUserId != null && comment.author_user_id === currentUserId));

  // A collapsed bubble opens on a click anywhere in the box, not just
  // on the chevron (Greg, 2026-07-30 - the chevron is a hard target on
  // a phone). Only while collapsed: once open, clicks belong to the
  // body, where the agent is selecting text and following links.
  //
  // Clicks that land on a real control inside the header (Edit,
  // Delete, the chevron itself) are left alone - the chevron already
  // toggles, so swallowing its click here would toggle twice and look
  // broken.
  function handleCollapsedBoxClick(e: React.MouseEvent<HTMLDivElement>): void {
    if (bodyOpen || !isCollapsible) return;
    if ((e.target as HTMLElement).closest("button, a, input, select")) return;
    setOpen(true);
  }

  const collapsedBoxIsClickable = isCollapsible && !bodyOpen;

  return (
    <div
      id={`agent-comment-${comment.id}`}
      data-author-kind={comment.author_kind}
      data-is-private={comment.is_private ? "true" : "false"}
      data-comment-id={comment.id}
      onClick={handleCollapsedBoxClick}
      // ``data-recently-focused`` is toggled by AuditTimeline's
      // click-to-scroll handler to briefly draw the agent's eye to the
      // comment they jumped to. Styled via the global rule in
      // index.css so we don't need a state hook here.
      //
      // No role="button" on the container: it holds its own buttons,
      // and nesting interactive roles is worse for a screen reader than
      // the chevron already being the labelled control.
      className={`group relative rounded-lg border px-4 py-3 transition-colors duration-700 ${containerClasses} ${
        collapsedBoxIsClickable
          ? // `mobile` scope keeps its body mounted for desktop, so the
            // pointer affordance has to stop at the same breakpoint the
            // collapse does.
            collapseScope === "mobile"
            ? "cursor-pointer max-lg:hover:brightness-[0.98] lg:cursor-default"
            : "cursor-pointer hover:brightness-[0.98]"
          : ""
      }`}
    >
      <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
        {/* Collapse toggle. Only rendered when the bubble is collapsible
            (read-view, not an editor). Hidden on desktop for the
            mobile-only scope; always visible for API notes, which
            collapse at every width. */}
        {isCollapsible && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={`${
              collapseScope === "mobile" ? "lg:hidden " : ""
            }-ml-1 rounded p-1 text-neutral-500 hover:bg-neutral-200/60`}
            aria-expanded={bodyOpen}
            aria-label={bodyOpen ? "Collapse message" : "Expand message"}
            data-testid="bubble-toggle"
          >
            <i
              className={`fa-duotone fa-thin ${
                bodyOpen ? "fa-chevron-down" : "fa-chevron-right"
              }`}
              aria-hidden="true"
            />
          </button>
        )}
        <span className="font-medium text-neutral-800">
          {comment.author_name || comment.author_email || "Unknown"}
        </span>
        {badge}
        <span className="text-neutral-400">
          {fmtTimestamp(comment.created_at)}
        </span>
        {comment.updated_at &&
          new Date(comment.updated_at).getTime() -
            new Date(comment.created_at).getTime() >
            2000 && (
            <span
              data-testid="edited-marker"
              className="text-neutral-400"
              title={`Edited ${fmtTimestamp(comment.updated_at)}`}
            >
              {/* A draft's time is creation, so a bare "(edited)" gave
                  no sign that Save Draft had just worked (Greg,
                  2026-09-30). The save time is the useful fact there. */}
              {isAiDraft
                ? `(saved ${fmtTimestamp(comment.updated_at)})`
                : "(edited)"}
            </span>
          )}
        {canMutate && !showEditor && (
          <span className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <button
              type="button"
              onClick={() => onEdit(comment.id)}
              className="rounded px-1.5 py-0.5 text-xs text-neutral-600 hover:bg-amber-100 hover:text-amber-900"
              data-testid="comment-edit"
              aria-label="Edit note"
            >
              <i
                className="fa-duotone fa-thin fa-pen-to-square mr-1"
                aria-hidden="true"
              />
              Edit
            </button>
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              disabled={isDeletingThis}
              className="rounded px-1.5 py-0.5 text-xs text-neutral-600 hover:bg-red-100 hover:text-red-800 disabled:cursor-progress disabled:opacity-60"
              data-testid="comment-delete"
              aria-label="Delete note"
            >
              <i
                className="fa-duotone fa-thin fa-trash mr-1"
                aria-hidden="true"
              />
              Delete
            </button>
          </span>
        )}
        {/* Split: a new topic that arrived as a reply-all on this thread
            moves out from its first email (IP2 / RM8, 2026-09-30).
            Move: one message that belongs on another existing ticket. */}
        {(onSplitFrom || onMove) && !showEditor && (
            <span className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
              {onSplitFrom &&
                comment.author_kind === "customer" &&
                !comment.is_private &&
                !comment.is_draft && (
                  <button
                    type="button"
                    onClick={() => onSplitFrom(comment.id)}
                    className="rounded px-1.5 py-0.5 text-xs text-neutral-600 hover:bg-primary-100 hover:text-primary-900"
                    data-testid="comment-split"
                    aria-label="Split into a new ticket from this email"
                    title="Move this email and its replies into a new ticket"
                  >
                    <i
                      className="fa-duotone fa-thin fa-scissors mr-1"
                      aria-hidden="true"
                    />
                    Split from here
                  </button>
                )}
              {onMove && (
                <button
                  type="button"
                  onClick={() => onMove(comment)}
                  className="rounded px-1.5 py-0.5 text-xs text-neutral-600 hover:bg-primary-100 hover:text-primary-900"
                  data-testid="comment-move"
                  aria-label="Move this message to another ticket"
                  title="Move this message to another existing ticket"
                >
                  <i
                    className="fa-duotone fa-thin fa-arrow-right-arrow-left mr-1"
                    aria-hidden="true"
                  />
                  Move
                </button>
              )}
            </span>
          )}
        {/* AI drafts are always in edit mode; surface a Discard
            affordance up here so the agent can throw the draft away
            without scrolling past Send Reply / Save Draft. */}
        {isAiDraft && canMutate && (
          <span className="ml-auto flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              disabled={isDeletingThis}
              className="rounded px-1.5 py-0.5 text-xs text-neutral-600 hover:bg-red-100 hover:text-red-800 disabled:cursor-progress disabled:opacity-60"
              data-testid="comment-delete"
              aria-label="Discard draft"
            >
              <i
                className="fa-duotone fa-thin fa-trash mr-1"
                aria-hidden="true"
              />
              Discard
            </button>
          </span>
        )}
      </div>
      {/* Collapsed preview, for the all-widths scope only. The
          mobile-only scope deliberately has none: its body stays in the
          DOM for desktop, so a preview would duplicate that text at
          every width instead of standing in for it. */}
      {isCollapsible && !bodyOpen && collapseScope === "all" && collapsedSnippet && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="bubble-snippet"
          className="block w-full truncate text-left text-xs text-neutral-500 hover:text-neutral-700"
        >
          {collapsedSnippet}
        </button>
      )}
      {/* Collapse wrapper. A closed all-widths bubble renders nothing
          at all - that is the case with many long AI notes per ticket,
          and their HTML is worth keeping out of the DOM entirely. The
          mobile-only scope has to keep its body mounted because
          desktop shows it via `lg:block` with no JS involved. Editors
          force `bodyOpen` true so a draft / active edit is never
          hidden. */}
      {(bodyOpen || collapseScope === "mobile") && (
      <div className={bodyOpen ? "block" : "hidden lg:block"}>
      {showEditor ? (
        <div className="space-y-2">
          {isAiDraft && requesterEmail ? (
            <RecipientHeader
              ticketNumber={ticketNumber}
              fromEmail={fromEmail}
              fromName={fromName}
              requesterEmail={requesterEmail}
              requesterName={requesterName ?? ""}
              toEmails={toEmails}
              ccEmails={ccEmails}
              bccEmails={bccEmails}
              onAddTo={(e) =>
                setToEmails((prev) =>
                  prev.some((x) => x.toLowerCase() === e.toLowerCase())
                    ? prev
                    : [...prev, e],
                )
              }
              onRemoveTo={(e) =>
                setToEmails((prev) =>
                  prev.filter((x) => x.toLowerCase() !== e.toLowerCase()),
                )
              }
              onAddCc={(e) =>
                setCcEmails((prev) =>
                  prev.some((x) => x.toLowerCase() === e.toLowerCase())
                    ? prev
                    : [...prev, e],
                )
              }
              onRemoveCc={(e) =>
                setCcEmails((prev) =>
                  prev.filter((x) => x.toLowerCase() !== e.toLowerCase()),
                )
              }
              onClearCc={() => setCcEmails([])}
              onAddBcc={(e) =>
                setBccEmails((prev) =>
                  prev.some((x) => x.toLowerCase() === e.toLowerCase())
                    ? prev
                    : [...prev, e],
                )
              }
              onRemoveBcc={(e) =>
                setBccEmails((prev) =>
                  prev.filter((x) => x.toLowerCase() !== e.toLowerCase()),
                )
              }
              subject={draftSubject}
              onChangeSubject={setDraftSubject}
              isPending={Boolean(isSendingDraft) || isSaving}
            />
          ) : null}
          <RichTextEditor
            value={draftHtml}
            onChange={(html, text) => {
              setDraftHtml(html);
              setDraftText(text);
            }}
            // Drafts are typically full email replies, so give them
            // the same vertical room the new-public-reply composer
            // has. Internal-note edits stay compact.
            minHeight={isAiDraft ? "24rem" : undefined}
            placeholder={
              isAiDraft
                ? "Review and edit the AI draft before sending..."
                : "Edit your internal note..."
            }
            ariaLabel={isAiDraft ? "Edit AI draft reply" : "Edit internal note"}
            // Drafts go out as email replies, so they take pasted
            // screenshots like the new-reply composer. Internal-note
            // edits stay text-only.
            onUploadImage={isAiDraft ? onUploadInlineImage : undefined}
          />
          {/* Files on the draft live INSIDE the editor, above the
              buttons, next to the dropzone that adds more. The
              read-only "Attachments" strip at the bottom of the
              bubble is gated off while the editor is open (see
              `!showEditor` below) so nothing renders twice. */}
          {isAiDraft && onUploadDraftFiles && (
            <>
              {attachments.length > 0 && (
                <div
                  data-testid="draft-attachments"
                  className="space-y-2 rounded border border-neutral-200 bg-white p-3"
                >
                  <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Attached to this reply
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {attachments.map((a) => (
                      <AttachmentTile
                        key={a.id}
                        attachment={a}
                        ticketNumber={ticketNumber}
                        onRemove={onRemoveAttachment}
                      />
                    ))}
                  </div>
                </div>
              )}
              {/* `value={[]}` keeps the dropzone a pure picker: its own
                  chip list would be a second view of "attached". */}
              <div data-testid="draft-dropzone">
                <AttachmentDropzone
                  value={[]}
                  onChange={(files) => {
                    void attachDroppedFiles(files);
                  }}
                  compact
                />
              </div>
              {isAttaching && (
                <p data-testid="draft-uploading" className="text-xs text-neutral-500">
                  Uploading...
                </p>
              )}
              {attachError && <Alert variant="error">{attachError}</Alert>}
            </>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {isAiDraft && onSendDraft ? (
              <>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  data-testid="draft-send"
                  onClick={() => {
                    void onSendDraft(
                      comment.id,
                      draftHtml,
                      draftText,
                      {
                        to_emails: toEmails,
                        cc_emails: ccEmails,
                        bcc_emails: bccEmails,
                      },
                      draftSubject.trim() ? draftSubject.trim() : undefined,
                    );
                  }}
                  isLoading={Boolean(isSendingDraft)}
                  // Outbound reads the attachment set at send time;
                  // sending mid-upload would mail without the file.
                  disabled={!draftText.trim() || isSaving || isAttaching}
                >
                  Send Reply
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="draft-save"
                  onClick={() => {
                    // Subject goes as typed, "" included: the backend
                    // stores "" as "use the default [#code] subject".
                    void onSaveEdit(draftHtml, draftText, comment.id, {
                      to_emails: toEmails,
                      cc_emails: ccEmails,
                      bcc_emails: bccEmails,
                      subject: draftSubject.trim(),
                    });
                  }}
                  isLoading={isSaving}
                  disabled={
                    !draftText.trim() || Boolean(isSendingDraft) || isAttaching
                  }
                >
                  Save Draft
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 active:bg-amber-800"
                  onClick={() => {
                    void onSaveEdit(draftHtml, draftText, comment.id);
                  }}
                  isLoading={isSaving}
                  disabled={!draftText.trim()}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* As-sent subject sits between the badge row and the body
              when present. Null on internal notes and on every row
              that pre-dates the 20260519_0021 migration; the
              fragment renders nothing in either case. */}
          {comment.subject && !comment.is_private && (
            <div
              data-testid="comment-subject"
              className="mb-1 text-xs text-neutral-600"
            >
              <span className="font-semibold uppercase tracking-wide text-neutral-500">
                Subject:
              </span>{" "}
              <span className="text-neutral-800">{comment.subject}</span>
            </div>
          )}
          {comment.body_html ? (
            <div
              className="prose prose-sm max-w-none text-sm text-neutral-800"
              dangerouslySetInnerHTML={bodyHtmlProps}
            />
          ) : (
            <div className="whitespace-pre-wrap text-sm text-neutral-800">
              {comment.body_text}
            </div>
          )}
        </>
      )}
      {!isEditing &&
        (comment.to_emails.length > 0 ||
          comment.cc_emails.length > 0 ||
          comment.bcc_emails.length > 0) && (
          <div className="mt-2 border-t border-neutral-100 pt-2 text-xs text-neutral-500">
            {comment.to_emails.length > 0 && (
              <div>To: {comment.to_emails.join(", ")}</div>
            )}
            {comment.cc_emails.length > 0 && (
              <div>CC: {comment.cc_emails.join(", ")}</div>
            )}
            {comment.bcc_emails.length > 0 && (
              <div>BCC: {comment.bcc_emails.join(", ")}</div>
            )}
          </div>
        )}
      {/* `!showEditor`, not `!isEditing`: an AI draft opens its editor
          without isEditing, and its files are shown inside the editor
          instead. Sent replies and non-editing bubbles keep this strip. */}
      {!showEditor && attachments.length > 0 && (
        <div
          data-testid="comment-attachments"
          className="mt-3 border-t border-neutral-100 pt-3"
        >
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
            Attachments
          </div>
          <div className="flex flex-wrap gap-3">
            {attachments.map((a) => (
              <AttachmentTile
                key={a.id}
                attachment={a}
                ticketNumber={ticketNumber}
                onAttachToResponse={onAttachToResponse}
                onRemove={onRemoveAttachment}
                isAttachedToDraft={attachedToDraftIds?.has(a.id) ?? false}
              />
            ))}
          </div>
        </div>
      )}
      </div>
      )}
    </div>
  );
}

export function TicketDetailAgentPage(): JSX.Element {
  // ``code`` from the URL bar - any of "ku1", "f-104944", or a bare
  // ticket_number for back-compat. The detail fetch resolves any of
  // the three forms; everything else keys on ticket_number (read
  // off the loaded ticket).
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAuthStore((s) => s.user);

  // Global search box: submitting navigates to the inbox with the query
  // running (the inbox seeds its search box from ``?q=``). Lets an agent
  // search all tickets from the detail page, exactly like the tickets
  // page's search box.
  const [headerSearch, setHeaderSearch] = useState("");
  const submitHeaderSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const q = headerSearch.trim();
      if (q) navigate(`/tickets?q=${encodeURIComponent(q)}`);
    },
    [headerSearch, navigate],
  );

  const detail = useAgentTicketDetail(code ?? "", {
    enabled: (code ?? "").length > 0,
  });

  // Merged tombstone -> master redirect (2026-07-15). A merged ticket
  // is "as if it didn't exist anymore" (Greg's framing): opening it
  // quietly lands on the surviving master, with a small notice carried
  // via router state so the master page can say where you came from.
  // Runs BEFORE URL canonicalization; the canonical effect skips
  // tombstones so the two navigations never compete.
  const mergedIntoCode = detail.data?.merged_into_public_code ?? null;
  const mergedTombstoneCode = detail.data?.public_code;
  useEffect(() => {
    if (!mergedIntoCode) return;
    navigate(`/tickets/${mergedIntoCode.toLowerCase()}`, {
      replace: true,
      state: { mergedFrom: mergedTombstoneCode },
    });
  }, [mergedIntoCode, mergedTombstoneCode, navigate]);

  // Post-merge inline notice (full message text). Set on arrival from
  // a tombstone redirect (via router state) or after merging other
  // tickets INTO this one. Read once from router state and kept
  // locally so it survives the state-clearing replace and can be
  // dismissed.
  const [mergeNotice, setMergeNotice] = useState<string | null>(null);
  // Reset on every :code change FIRST: react-router reuses this
  // component instance across param navigations (Recently-viewed rail
  // links land here without a remount), and a notice about ticket A
  // must never survive onto ticket B's page. Source order matters -
  // this effect runs before the state-reading one below, so an
  // arrival that changes code AND carries mergedFrom still ends the
  // commit with the fresh notice set.
  useEffect(() => {
    setMergeNotice(null);
  }, [code]);
  useEffect(() => {
    const state = location.state as { mergedFrom?: string } | null;
    if (state?.mergedFrom) {
      setMergeNotice(
        `Ticket [#${state.mergedFrom}] was merged into this ticket. ` +
          "Replies to it now land here.",
      );
      // Clear the router state so a refresh does not re-announce;
      // preserve search + hash so future deep-link params survive.
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        { replace: true, state: null },
      );
    }
  }, [
    location.state,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);

  // Canonicalize the URL bar to the public_code form once we have a
  // ticket. /tickets/200000 -> /tickets/ku1, /tickets/F-104944 stays
  // lowercased to /tickets/f-104944. Replace so the back button
  // doesn't bounce. Skipped for merged tombstones - the merge
  // redirect above owns that navigation.
  const ticketPublicCode = detail.data?.public_code;
  useEffect(() => {
    if (!ticketPublicCode || !code || mergedIntoCode) return;
    const canonical = ticketPublicCode.toLowerCase();
    if (code.toLowerCase() !== canonical) {
      navigate(`/tickets/${canonical}`, { replace: true });
    }
  }, [ticketPublicCode, code, navigate, mergedIntoCode]);

  const ticketNumber = detail.data?.ticket_number ?? -1;
  const ticketLoaded = ticketNumber > 0;

  usePageTitle(
    detail.data
      ? `${detail.data.public_code} - ${detail.data.subject ?? "Ticket"} - Buyist Support`
      : "Ticket - Buyist Support",
  );

  // Conversation window. False (the default on every arrival) fetches
  // the newest DEFAULT_BODY_WINDOW bodies; true fetches the whole
  // thread. Reset per ticket by the effect below so navigating from a
  // ticket whose history the agent opened does not drag the expanded
  // state onto the next one.
  const [historyExpanded, setHistoryExpanded] = useState(false);
  useEffect(() => {
    setHistoryExpanded(false);
  }, [ticketNumber]);

  const comments = useAgentTicketComments(ticketNumber, {
    enabled: ticketLoaded,
    bodyWindow: historyExpanded ? null : DEFAULT_BODY_WINDOW,
  });
  const attachments = useAgentTicketAttachments(ticketNumber, {
    enabled: ticketLoaded,
  });
  const signatureQuery = useReplySignature();
  const watchers = useTicketWatchers(ticketNumber, {
    enabled: ticketLoaded,
  });
  // The audit timeline in the right rail already fetches these via the
  // same query key (limit defaults to 20), so react-query dedupes the
  // request. We re-read here so the header can surface a
  // "Forwarded by <agent>" badge when the ticket was created via the
  // Phase 8c agent-forward branch (SPEC §10.4 / §6
  // ticket_created_via_agent_forward event).
  const ticketEvents = useTicketEvents(ticketNumber, {
    enabled: ticketLoaded,
  });

  // Audit-timeline jump into not-yet-loaded history. The timeline hands
  // us a comment id whose bubble is not in the DOM; we load the rest of
  // the thread and re-run the SAME jump once that bubble exists. Held as
  // state rather than a ref because the retry has to be driven by the
  // arrival of the comments data, which is a render, not a callback.
  const [pendingRevealCommentId, setPendingRevealCommentId] = useState<
    string | null
  >(null);
  const handleRequestCommentReveal = useCallback((commentId: string) => {
    setHistoryExpanded(true);
    setPendingRevealCommentId(commentId);
  }, []);
  useEffect(() => {
    if (!pendingRevealCommentId) return;
    const rows = comments.data ?? [];
    const target = rows.find((c) => c.id === pendingRevealCommentId);
    // Not in this thread at all (a deleted comment still has timeline
    // rows). Nothing to jump to, ever - stop waiting.
    if (rows.length > 0 && !target) {
      setPendingRevealCommentId(null);
      return;
    }
    // Still the windowed payload; the body has not arrived yet.
    if (!target || target.body_omitted) return;
    if (focusCommentAnchor(pendingRevealCommentId)) {
      setPendingRevealCommentId(null);
    }
  }, [pendingRevealCommentId, comments.data]);

  const updateTicket = useUpdateTicket(ticketNumber);
  const addComment = useAddAgentComment(ticketNumber);
  // Sticky ticket CC: a sent public reply reconciles the CC / BCC
  // watcher rows to match what went out (see reconcileWatchersToReply).
  const addWatcher = useAddWatcher(ticketNumber);
  const removeWatcher = useRemoveWatcher(ticketNumber);
  const editComment = useEditAgentComment(ticketNumber);
  const sendDraftComment = useSendDraftComment(ticketNumber);
  const deleteComment = useDeleteAgentComment(ticketNumber);
  const uploadAttachment = useUploadAgentAttachment(ticketNumber);
  const attachExistingToDraft = useAttachExistingToDraft(ticketNumber);
  const deleteAttachment = useDeleteAgentAttachment(ticketNumber);
  const markSpam = useMarkSpam(ticketNumber);
  const softDeleteTicket = useSoftDeleteTicket(ticketNumber);
  const queryClient = useQueryClient();

  // Per-bubble edit / delete state. Only one note is editable at a
  // time; the confirmation modal tracks which delete is pending.
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null);
  const [deleteCommentError, setDeleteCommentError] = useState<string | null>(
    null,
  );

  // "Attach to Response" picks - the agent clicked the paperclip icon
  // on one or more earlier attachments. We keep the full row in state
  // so we can show the filename in the staged-attachments strip; the
  // backend only needs the ids on send. Cleared every time a comment
  // is sent OR the composer is canceled.
  const [draftAttachmentRefs, setDraftAttachmentRefs] = useState<
    AttachmentResponse[]
  >([]);
  // Remove-attachment confirmation modal. The hover button on any
  // tile opens it; the modal owns "are you sure" + the delete call.
  const [removingAttachment, setRemovingAttachment] =
    useState<AttachmentResponse | null>(null);
  const [removeAttachmentError, setRemoveAttachmentError] = useState<
    string | null
  >(null);

  const [tab, setTab] = useState<ComposerTab>(() =>
    ticketLoaded ? readComposerTab(ticketNumber) : "public",
  );
  useEffect(() => {
    if (ticketLoaded) writeComposerTab(ticketNumber, tab);
  }, [tab, ticketNumber]);

  // Seed the composer CC / BCC lists from the ticket's persistent
  // watcher set ("sticky ticket CC", Greg's call 2026-07-02). The CC /
  // BCC watcher rows ARE the ticket's participant list: the inbound
  // parser adds them from every customer Cc line, and each sent reply
  // reconciles them (see reconcileWatchersToReply). Seeding from the
  // watchers - NOT from the most recent comment's cc_emails - is what
  // keeps an established participant on the thread even after a customer
  // replies without Cc'ing them.
  //
  // This reverts the "seed from the most recent public comment" rule
  // (locked 2026-05-20). That rule dropped participants whenever a
  // message in the thread carried an empty Cc line: on ticket MX4, Josh
  // was a Cc watcher, but he emailed in without a Cc, the next reply
  // seeded off that empty Cc, and the empty Cc then propagated to every
  // later reply. Same class of bug the To-line "LH3" fix already fixed
  // for To (computeSeedTo ignores a customer's inbound recipients); the
  // Cc path just never got the same treatment. The 2026-05-20 rule's
  // real goal - a Cc the agent removed must stay removed (F-104409) - is
  // now met by making removal an actual unsubscribe: dropping a chip and
  // sending deletes the watcher, so it is gone from the next seed too.
  //
  // The current To recipients / requester are excluded (they belong on
  // the To line, not Cc) and our own outbound mailbox is self-
  // suppressed. Comparison is case-insensitive. The AI-draft bubble no
  // longer reads these: a draft's audience lives on its own row (see
  // initialCc in AgentCommentBubble).
  const { ccCandidates, bccCandidates } = useMemo(() => {
    if (!detail.data || !comments.data) {
      return { ccCandidates: [] as string[], bccCandidates: [] as string[] };
    }
    const watcherCc = (watchers.data ?? [])
      .filter((w) => w.kind === "cc")
      .map((w) => w.email.toLowerCase());
    const watcherBcc = (watchers.data ?? [])
      .filter((w) => w.kind === "bcc")
      .map((w) => w.email.toLowerCase());

    const seedTo = computeSeedTo(comments.data, detail.data.requester_email);

    // Self-suppression: never pre-populate our own outbound mailbox on
    // CC / BCC. A watcher row carrying our own address used to propagate
    // forward and Greg had to remove it on every reply. The configured
    // From-email is the canonical "self" address; we also strip the
    // hard-coded support@buyist.com in case the From config is loading
    // or unset.
    const selfAddresses = new Set<string>(
      [
        signatureQuery.data?.from_email?.toLowerCase(),
        "support@buyist.com",
      ].filter((e): e is string => Boolean(e)),
    );

    const keep = (e: string) => !seedTo.includes(e) && !selfAddresses.has(e);
    const ccs = Array.from(new Set(watcherCc)).filter(keep);
    const bccs = Array.from(new Set(watcherBcc)).filter(keep);
    return { ccCandidates: ccs, bccCandidates: bccs };
  }, [detail.data, comments.data, watchers.data, signatureQuery.data]);

  useEffect(() => {
    if (!ticketLoaded || seededForTicketRef.current === ticketNumber) {
      return;
    }
    if (!detail.data || !comments.data) return;
    // Wait for the watchers query to settle before latching the seed.
    // For the FIRST public reply on a ticket (no prior non-private
    // comment) the CC line is sourced entirely from the Cc watcher rows
    // the inbound parser captured off the original email. Those load via
    // a separate query; latching on comments + detail alone seeded an
    // empty CC and then the seededForTicketRef guard blocked the re-seed
    // when the watcher data arrived a beat later, silently dropping a
    // captured CC. ``isFetched`` (not ``isSuccess``) so an errored or
    // genuinely-empty watcher list still lets the seed proceed.
    if (!watchers.isFetched) return;
    const seedTo = computeSeedTo(comments.data, detail.data.requester_email);

    setToEmails(seedTo);
    setCcEmails(ccCandidates);
    setBccEmails(bccCandidates);
    seededForTicketRef.current = ticketNumber;
  }, [ticketNumber, detail.data, comments.data, watchers.data, watchers.isFetched, ccCandidates, bccCandidates]);

  // Per-tab composer drafts. Public Reply and Internal Note are
  // visually the same editor but each tab owns its own underlying
  // content. This lets us:
  //   - keep the reply-signature confined to the Public tab so an
  //     internal note never inherits "Thanks for reaching out!" prose,
  //   - let the agent switch tabs without losing typed content on the
  //     other side,
  //   - keep ``hasDraft`` (which controls the composer's auto-expand)
  //     reading only the active tab so a seeded Public signature on
  //     one ticket does not auto-pop the composer on the next ticket.
  const [publicDraftHtml, setPublicDraftHtml] = useState("");
  const [publicDraftText, setPublicDraftText] = useState("");
  const [internalDraftHtml, setInternalDraftHtml] = useState("");
  const [internalDraftText, setInternalDraftText] = useState("");
  // Per-tab subject draft. Only meaningful on the Public Reply tab
  // (internal notes don't email). Seeded once per (ticket, public_code,
  // ticket_subject) tuple from ``[#${public_code}] ${ticket.subject}``
  // so the agent sees what the recipient's mailbox will show; they
  // can delete the ``[#code]`` and the backend's
  // ``_ensure_ticket_token_in_subject`` restores it at send time.
  const [publicSubjectDraft, setPublicSubjectDraft] = useState<string>("");
  // Re-seed when the ticket loads OR when ticket.subject / public_code
  // change underneath us (e.g. after a previous reply renamed the
  // ticket, or another agent edited it in a different tab). We keep
  // the agent's in-progress edits if the seed value hasn't changed.
  const subjectSeededForRef = useRef<string | null>(null);
  const draftHtml = tab === "public" ? publicDraftHtml : internalDraftHtml;
  const draftText = tab === "public" ? publicDraftText : internalDraftText;
  // Wrapped setters: a single setDraft* call writes to whichever tab
  // is active at the moment of the call. The function identity changes
  // per render (closure captures ``tab``), but that's fine because
  // none of our hooks include them in deps - they only get invoked
  // from event handlers and the editor's onChange.
  const setDraftHtml = (v: string) => {
    if (tab === "public") setPublicDraftHtml(v);
    else setInternalDraftHtml(v);
  };
  const setDraftText = (v: string) => {
    if (tab === "public") setPublicDraftText(v);
    else setInternalDraftText(v);
  };
  const [draftFiles, setDraftFiles] = useState<File[]>([]);
  // Agents to email when an Internal Note is saved (Greg's task 4). The
  // backend validates these are agents and notifies them; the assignee
  // is never changed. Reset on ticket change so a selection on one
  // ticket never leaks to the next (the page instance is reused across
  // /tickets/:code navigations, like the other composer drafts).
  const [notifyAgentEmails, setNotifyAgentEmails] = useState<string[]>([]);
  useEffect(() => {
    setNotifyAgentEmails([]);
  }, [ticketNumber]);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [spamOpen, setSpamOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [splitStartId, setSplitStartId] = useState<string | null>(null);
  const [moveComment, setMoveComment] = useState<CommentResponse | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Mobile-only collapse for the bottom-most "Original ticket" entry.
  // Collapsed by default on phones (the redesign collapses the whole
  // thread); always expanded on desktop via `lg:` classes. Page-level
  // because the Original entry is inline JSX, not a bubble component.
  const [originalOpenMobile, setOriginalOpenMobile] = useState(false);
  // Mobile-only: whether the docked Properties sheet is expanded. The
  // Properties form lives in a bar pinned to the bottom of the viewport
  // on phones (< lg); tapping the peek expands the full form. Collapsed
  // by default. Inert on desktop where the same form renders as the
  // normal right-rail card via `lg:` resets.
  const [dockOpen, setDockOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Close-button progress flag. While true the header's Close button
  // renders Button's built-in spinner + the label "Closing..." instead
  // of the static check icon, the button is disabled, and the
  // handler is waiting on (a) the PATCH status=closed AND (b) a
  // minimum 1500 ms hold so the agent sees the affordance flip
  // before we navigate away. Reset to false only when the close
  // fails - on success the page is about to unmount via navigate().
  const [isClosing, setIsClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  // Current Status save. Tracked separately from updateTicket.isPending
  // because that same mutation drives the Properties dropdowns - without
  // this flag, changing the priority would spin the Current Status card's
  // Save button too.
  const [currentStatusSaving, setCurrentStatusSaving] = useState(false);
  const [currentStatusError, setCurrentStatusError] = useState<string | null>(
    null,
  );
  const handleSaveCurrentStatus = useCallback(
    async (html: string): Promise<void> => {
      setCurrentStatusError(null);
      setCurrentStatusSaving(true);
      try {
        // "" clears the field server-side. Any change here emails the
        // assignee, same as an AI-driven update through the API.
        await updateTicket.mutateAsync({ current_status_html: html });
      } catch (err) {
        setCurrentStatusError(
          err instanceof Error
            ? err.message
            : "Could not save the current status.",
        );
        throw err;
      } finally {
        setCurrentStatusSaving(false);
      }
    },
    [updateTicket],
  );

  // Header title rename. The editor owns its own saving / error state,
  // so this is just the PATCH - it rethrows so the editor stays open
  // with the agent's text when the save fails.
  const handleSaveSubject = useCallback(
    async (next: string): Promise<void> => {
      await updateTicket.mutateAsync({ subject: next });
    },
    [updateTicket],
  );

  // Paste-into-composer inline-image upload. The RichTextEditor's
  // handlePaste intercepts image clipboard files and calls this; we
  // POST the blob to the inline-images endpoint and return the public
  // /i/{token} URL the editor then inserts as a ResizableImage node.
  const uploadInlineImage = useCallback(
    async (file: File): Promise<string> => {
      const form = new FormData();
      form.append("file", file, file.name || "pasted-image");
      const response = await api.post<{ url: string }>(
        `/agent/tickets/${ticketNumber}/inline-images`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data.url;
    },
    [ticketNumber],
  );

  // Composer collapses to a single-line "Type your reply" affordance
  // until the agent clicks into it or presses Reply/Forward in the
  // header. This reclaims ~290px of the top fold for the conversation
  // thread.
  const [composerExpanded, setComposerExpanded] = useState(false);
  const composerRef = useRef<HTMLDivElement>(null);
  // ``hasDraft`` reflects ONLY the active tab so a signature seeded
  // into Public never makes Internal Note look "in progress" (and
  // vice versa). It's read from publicDraftText/internalDraftText
  // directly rather than the derived ``draftText`` so React doesn't
  // need to recompute the visible editor before answering.
  const hasDraft =
    (tab === "public" ? publicDraftText : internalDraftText).trim().length > 0;

  // Reply-signature prefill. Fires LAZILY: only once the agent has
  // expanded the composer on the Public tab, and only into the public
  // draft state. Internal Note tab keeps its own (empty) draft. This
  // is what enforces:
  //   - #172: the composer stays collapsed by default because seeding
  //     used to put signature prose into draftText, which then set
  //     hasDraft=true and auto-expanded the composer on mount.
  //   - #178: the signature never appears on Internal Note because
  //     the internal tab has its own state which we never seed.
  // ``signatureSeededForTicketRef`` makes this one-shot per ticket:
  // re-collapsing the composer and re-expanding it doesn't re-seed,
  // and switching tabs doesn't re-seed either.
  const signatureSeededForTicketRef = useRef<number | null>(null);
  useEffect(() => {
    if (!ticketLoaded) return;
    if (signatureSeededForTicketRef.current === ticketNumber) return;
    if (!composerExpanded) return;
    if (tab !== "public") return;
    if (!detail.data) return;
    if (!signatureQuery.data) return;
    if (publicDraftText.trim().length > 0) return;

    // The requester's CURRENT name, not the creation-time snapshot
    // (which is the email address whenever the inbound mail carried
    // no display name). See the backend resolver.
    const firstName = firstNameOf(
      detail.data.requester_display_name || detail.data.requester_name,
    );
    const html = renderReplySignatureHtml(
      signatureQuery.data.template,
      firstName,
    );
    setPublicDraftHtml(html);
    // Plain-text draft: substitute the placeholder but keep the line
    // breaks. The backend stores body_text separately from body_html
    // (SPEC §10), so this string is what plain-text email clients
    // see if the recipient prefers text/plain.
    const text = signatureQuery.data.template.replaceAll(
      "{first_name}",
      firstName || "there",
    );
    setPublicDraftText(text);
    signatureSeededForTicketRef.current = ticketNumber;
  }, [
    ticketNumber,
    tab,
    composerExpanded,
    detail.data,
    signatureQuery.data,
    publicDraftText,
  ]);

  // Subject seed. Fires whenever (a) the ticket loads, (b) the
  // ticket's public_code or subject changes (rename via a previous
  // reply, agent rename, etc.). Re-seed key is the concatenation so
  // an unchanged seed-string leaves the agent's in-progress edit
  // untouched. We re-seed even when the composer is collapsed so the
  // first expand on the next ticket shows a fresh subject without
  // flicker.
  useEffect(() => {
    if (!detail.data) return;
    const seedKey = `${detail.data.public_code}::${detail.data.subject}`;
    if (subjectSeededForRef.current === seedKey) return;
    subjectSeededForRef.current = seedKey;
    setPublicSubjectDraft(
      `[#${detail.data.public_code}] ${detail.data.subject ?? ""}`,
    );
  }, [detail.data]);
  const isComposerOpen = composerExpanded || hasDraft;

  // Composer-local recipient state. Pre-seeded once per ticket from
  // (a) the most recent public comment's cc_emails / bcc_emails and
  // (b) any persistent watchers, deduped against the requester. The
  // composer is the source of truth for this reply; on Send we pass
  // these arrays through to comment.to_emails / cc_emails / bcc_emails
  // so the outbound email goes to those exact addresses. The To list
  // defaults to [requester] but is editable - the agent can remove
  // the requester and add different addresses to re-route a single
  // reply without permanently mutating the ticket. Watchers are
  // still respected by the email pipeline (notification_service
  // unions the comment lists with live watchers), so a sticky
  // "always CC" watcher added elsewhere isn't lost.
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  // Once the seeding effect runs once per ticket, we leave the state
  // alone so the agent's edits aren't blown away by a refetch.
  const seededForTicketRef = useRef<number | null>(null);

  // Count EVERY row, including the ones whose body was left unloaded by
  // the window: the header is telling the agent how long this
  // conversation is, not how much of it is on screen.
  const conversationCount = comments.data?.length ?? 0;

  // Group attachments by comment_id once per attachments payload, so
  // each bubble's render does a dict lookup instead of an O(N) filter.
  // Ticket-level files (comment_id=null) are pulled out separately for
  // the Original ticket entry at the bottom of the thread.
  const { attachmentsByComment, ticketLevelAttachments } = useMemo(() => {
    const byComment = new Map<string, AttachmentResponse[]>();
    const ticketLevel: AttachmentResponse[] = [];
    for (const a of attachments.data ?? []) {
      if (a.comment_id) {
        const list = byComment.get(a.comment_id) ?? [];
        list.push(a);
        byComment.set(a.comment_id, list);
      } else {
        ticketLevel.push(a);
      }
    }
    return {
      attachmentsByComment: byComment,
      ticketLevelAttachments: ticketLevel,
    };
  }, [attachments.data]);

  // Newest first, and only the rows whose body actually came down the
  // wire. A row the server left unloaded (``body_omitted``) has no
  // content to render - rendering a header for it would be an empty
  // bubble, which is worse than an honest "load the rest" button. They
  // still count toward ``conversationCount`` and still feed the reply
  // To-line seed, because those read metadata, not bodies.
  const { renderedComments, omittedCount, latestCustomerCommentId } = useMemo(
    () => {
      const sorted = (comments.data ?? [])
        .slice()
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() -
            new Date(a.created_at).getTime(),
        );
      const rendered = sorted.filter((c) => !c.body_omitted);
      return {
        renderedComments: rendered,
        omittedCount: sorted.length - rendered.length,
        // The most-recent CUSTOMER message is auto-expanded on mobile so
        // an agent on a phone immediately sees what the client said;
        // everything else starts collapsed. Null when the loaded window
        // holds no customer reply - then every bubble collapses and the
        // agent taps to read, exactly as on an agent-created ticket.
        latestCustomerCommentId:
          rendered.find((c) => c.author_kind === "customer")?.id ?? null,
      };
    },
    [comments.data],
  );

  const ticket = detail.data;

  // Same reasoning as the per-bubble memo (see AgentCommentBubble): the
  // original description is sanitized once per distinct body instead of
  // once per keystroke. Declared up here because the render below sits
  // past three early returns.
  const descriptionHtmlProps = useMemo(
    () =>
      safeHtmlProps(detail.data?.description_html ?? "", {
        // Original-ticket body IS the first message in the thread - skip
        // the quote-collapser so a body that happens to lead with
        // <blockquote> or an Outlook "From:" header renders verbatim
        // instead of going behind a "..." pill.
        collapseQuotes: false,
      }),
    [detail.data?.description_html],
  );

  // "Mark spam" only makes sense on a fresh, untriaged ticket. Greg's
  // rule (2026-06-07): show it only when the ticket is unassigned AND has
  // no Type set (and is not already spam). A ticket that has an owner or
  // a Type is past the point where flagging the whole thing as spam is a
  // sensible call - it has weeks of responses and updates behind it.
  const canMarkSpam =
    !!ticket &&
    !ticket.spam &&
    ticket.assignee_user_id === null &&
    ticket.type === null;

  // Merge is offered only where the backend would accept it: not on
  // spam / deleted / already-merged tickets and not on Gemini archive
  // rows (all of those 409 server-side; hiding the button saves the
  // dead-end round trip). Merged tombstones auto-redirect anyway -
  // the check is belt-and-braces for the pre-redirect frame.
  const canMerge =
    !!ticket &&
    !ticket.spam &&
    !ticket.deleted_at &&
    !ticket.merged_into_public_code &&
    !/^g-/i.test(ticket.public_code);

  // The single ticket_created_via_agent_forward row, if any, sourced
  // from the audit timeline. The events list is newest-first so a
  // .find() walks past more recent rows; tickets only ever have ONE
  // creation event, so order doesn't matter in practice.
  const forwardedByEvent = useMemo(() => {
    return (ticketEvents.data ?? []).find(
      (e) => e.event_type === "ticket_created_via_agent_forward",
    );
  }, [ticketEvents.data]);

  const assigneeValue = useMemo<string>(() => {
    if (!ticket) return "unassigned";
    if (ticket.assignee_user_id === null) return "unassigned";
    if (currentUser && ticket.assignee_user_id === currentUser.id) return "mine";
    // Unknown agent UUID - we don't currently surface real names. The
    // raw UUID would not match any of our static options; fall back to
    // "mine" if it's the current user (handled above), otherwise pass
    // through the raw value so the Select shows the closest match.
    return ticket.assignee_user_id;
  }, [ticket, currentUser]);

  // Set of attachment IDs currently staged on the reply draft - used
  // by AttachmentTile to flip its paperclip icon into the "already
  // attached" state. MUST live above the early returns below so the
  // hook count stays stable across the loading -> loaded transition
  // (Rules of Hooks: hooks called must not depend on prior renders).
  //
  // An open AI draft counts too. Its files are server-side clones with
  // their own ids, so a source tile is matched to its clone by content
  // hash + filename.
  const openAiDraftId = useMemo(() => {
    const drafts = (comments.data ?? []).filter(
      (c) =>
        c.author_kind === "api_key" &&
        c.is_private &&
        !c.sent_via_email &&
        c.is_draft,
    );
    // Newest wins when the AI left more than one: that is the reply the
    // agent is about to send.
    drafts.sort((a, b) => a.created_at.localeCompare(b.created_at));
    return drafts.length > 0 ? drafts[drafts.length - 1].id : null;
  }, [comments.data]);
  const attachedToDraftIds = useMemo(() => {
    const ids = new Set(draftAttachmentRefs.map((a) => a.id));
    if (openAiDraftId) {
      const all = attachments.data ?? [];
      const onDraft = new Set(
        all
          .filter((a) => a.comment_id === openAiDraftId && a.sha256)
          .map((a) => `${a.sha256}:${a.filename}`),
      );
      for (const a of all) {
        if (
          a.comment_id !== openAiDraftId &&
          a.sha256 &&
          onDraft.has(`${a.sha256}:${a.filename}`)
        ) {
          ids.add(a.id);
        }
      }
    }
    return ids;
  }, [draftAttachmentRefs, openAiDraftId, attachments.data]);

  // Which Properties-card control most recently saved successfully.
  // Used to apply ``data-just-saved`` on the control so the
  // index.css ``buyist-save-success-pulse`` keyframe runs - SPEC §9
  // "green glow on save". Auto-clears after the keyframe duration
  // (1.4s) so repeat saves on the same control re-trigger the pulse.
  // Same hooks-above-early-return discipline as ``attachedToDraftIds``.
  const [recentlySavedField, setRecentlySavedField] = useState<
    "owner" | "status" | "priority" | "type" | "assignee" | "brand" | null
  >(null);
  const recentlySavedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  if (!code) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <Alert variant="error" title="Invalid ticket URL">
          The URL does not contain a ticket identifier.
        </Alert>
      </div>
    );
  }

  if (detail.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (detail.isError || !ticket) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <Alert variant="error" title="Ticket not found">
          We could not load this ticket. It may have been deleted or you may
          not have access.
        </Alert>
        <Link
          to="/tickets"
          className="mt-4 inline-block text-sm text-primary-700 hover:text-primary-800"
        >
          Back to Tickets
        </Link>
      </div>
    );
  }

  function flashSaved(
    field: "owner" | "status" | "priority" | "type" | "assignee" | "brand",
  ): void {
    setRecentlySavedField(field);
    if (recentlySavedTimerRef.current) {
      clearTimeout(recentlySavedTimerRef.current);
    }
    recentlySavedTimerRef.current = setTimeout(() => {
      setRecentlySavedField(null);
      recentlySavedTimerRef.current = null;
    }, 1500);
  }

  async function handleStatus(value: TicketStatus) {
    if (!ticket || value === ticket.status) return;
    await updateTicket.mutateAsync({ status: value });
    flashSaved("status");
  }

  async function handlePriority(value: TicketPriority) {
    if (!ticket || value === ticket.priority) return;
    await updateTicket.mutateAsync({ priority: value });
    flashSaved("priority");
  }

  async function handleType(rawValue: string) {
    if (!ticket) return;
    const next = rawValue === "" ? null : (rawValue as TicketType);
    if (next === ticket.type) return;
    await updateTicket.mutateAsync({ type: next });
    flashSaved("type");
  }

  async function handleAssignee(value: string) {
    if (!ticket) return;
    if (value === "unassigned") {
      await updateTicket.mutateAsync({ assignee_user_id: null });
      flashSaved("assignee");
      return;
    }
    if (value === "mine" && currentUser) {
      await updateTicket.mutateAsync({ assignee_user_id: currentUser.id });
      flashSaved("assignee");
      return;
    }
    // Agent UUID from the /agent/agents directory. PATCH it through
    // verbatim - the backend validates the FK and reflects the new
    // assignee on the next ticket fetch, at which point assigneeValue
    // re-computes to "mine" (if the agent picked themselves) or stays
    // as the UUID (someone else). Sanity-check it looks like a uuid so
    // a stray sentinel doesn't reach the API.
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (UUID_RE.test(value)) {
      await updateTicket.mutateAsync({ assignee_user_id: value });
      flashSaved("assignee");
    }
  }

  async function handleOwner(next: {
    id: string | null;
    email: string;
    name: string;
  }) {
    if (!ticket) return;
    if (!next.id) return;
    if (
      next.id === ticket.requester_user_id &&
      next.email === ticket.requester_email &&
      next.name === ticket.requester_name
    ) {
      return;
    }
    // PATCH all three fields together so the requester snapshot
    // (email, name) stays in sync with the requester_user_id FK.
    // ``ticket_service.update_fields`` emits requester_changed +
    // requester_email_changed events as appropriate.
    await updateTicket.mutateAsync({
      requester_user_id: next.id,
      requester_email: next.email,
      requester_name: next.name,
    });
    flashSaved("owner");
  }

  async function handlePostComment() {
    setComposeError(null);
    if (!draftText.trim()) {
      setComposeError("Type a message before sending.");
      return;
    }
    const isPrivate = tab === "internal";
    if (!ticket) return;
    // Only send a To override when the agent has actually changed it.
    // If the only To address is the requester (the seed default),
    // leave to_emails empty - the backend treats that as "use
    // ticket.requester_email" which is identical behavior with a
    // cleaner audit trail (the comment row doesn't carry a
    // redundant recipient).
    const requester = ticket.requester_email.toLowerCase();
    const isDefaultTo =
      toEmails.length === 1 && toEmails[0] === requester;
    const filesToUpload = draftFiles;
    const reattachIds = draftAttachmentRefs.map((a) => a.id);

    // Upload files to ticket-level FIRST, BEFORE creating the comment.
    // The previous flow (upload after comment create) lost the race
    // with the outbound-email send: the email fired inside the
    // POST /comments call and the customer received a text-only
    // message because no attachment row existed yet. Pre-uploading
    // means the full attachment set is known by the time the
    // comment POST hits the backend; the server links each one to
    // the new comment (no clone, no orphan ticket-level row) and
    // ``send_public_reply`` sees the complete set.
    //
    // Per-file errors are aggregated. If at least one file fails we
    // still post the comment (the agent doesn't lose their prose);
    // a banner surfaces which files need attention.
    const newAttachmentIds: string[] = [];
    const uploadedAttachments: AttachmentResponse[] = [];
    const uploadErrors: string[] = [];
    for (const f of filesToUpload) {
      try {
        const uploaded = await uploadAttachment.mutateAsync({
          file: f,
        });
        newAttachmentIds.push(uploaded.id);
        uploadedAttachments.push(uploaded);
      } catch (e: unknown) {
        const detail =
          (e as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail ??
          (e instanceof Error ? e.message : "upload failed");
        uploadErrors.push(`${f.name}: ${detail}`);
      }
    }

    try {
      const created = await addComment.mutateAsync({
        body_text: draftText,
        body_html: draftHtml || null,
        is_private: isPrivate,
        // Internal note: ``to_emails`` is the list of AGENTS to notify
        // (the backend validates they are agents and emails them; the
        // assignee is unchanged). Public reply: the To override, or []
        // when it is just the default requester.
        to_emails: isPrivate
          ? notifyAgentEmails
          : isDefaultTo
            ? []
            : toEmails,
        cc_emails: isPrivate ? [] : ccEmails,
        bcc_emails: isPrivate ? [] : bccEmails,
        // Per-reply subject override. Sent only on public replies;
        // internal notes have no subject concept (the backend
        // ignores the field for is_private=true regardless, but
        // dropping it client-side keeps the comment payload clean).
        // Empty string after trim collapses to undefined so a
        // missing-then-empty input lands on the deterministic
        // ``[#code] ticket.subject`` shape rather than persisting
        // an empty override.
        ...(!isPrivate && publicSubjectDraft.trim().length > 0
          ? { subject: publicSubjectDraft.trim() }
          : {}),
        // Both "Attach to Response" picks AND freshly-uploaded files
        // ride this field. The server distinguishes by source row
        // state: ``comment_id IS NULL`` -> link directly; otherwise
        // clone (preserves the prior comment's audit trail).
        attach_existing_ids: [...reattachIds, ...newAttachmentIds],
      });

      // Reconcile the attachments cache so the just-attached files
      // render under the new note immediately instead of after the
      // next 15s attachments poll. Files are uploaded ticket-level
      // (comment_id=null) BEFORE the comment exists; the POST above
      // links them server-side, but the attachments query was last
      // refreshed by the upload (still comment_id=null) and addComment
      // does not invalidate it. Without this the note appears with no
      // attachment for several seconds, which reads as "the attachment
      // did not save" and prompts the agent to re-attach.
      if (uploadedAttachments.length > 0) {
        // Discard the upload's in-flight refetch first: it would
        // resolve with the pre-link comment_id=null snapshot and
        // clobber the optimistic link written just below.
        await queryClient.cancelQueries({
          queryKey: agentTicketAttachmentsQueryKey(ticketNumber),
        });
        queryClient.setQueryData<AttachmentResponse[]>(
          agentTicketAttachmentsQueryKey(ticketNumber),
          (prev) => {
            const byId = new Map(
              (prev ?? []).map((a) => [a.id, a] as const),
            );
            for (const up of uploadedAttachments) {
              byId.set(up.id, { ...up, comment_id: created.id });
            }
            return Array.from(byId.values());
          },
        );
      }
      // Kick a reconciling refetch when any file rode this comment. The
      // optimistic write above covers freshly-uploaded files; the
      // refetch also pulls in "Attach to Response" clones, whose ids
      // are server-assigned and unknown on the client.
      if (uploadedAttachments.length > 0 || reattachIds.length > 0) {
        void queryClient.invalidateQueries({
          queryKey: agentTicketAttachmentsQueryKey(ticketNumber),
        });
      }

      // Sticky ticket CC: make the watcher set match the reply that just
      // went out (new CCs subscribe, dropped CCs unsubscribe). Public
      // replies only - an internal note is not addressed to anyone.
      if (!isPrivate) {
        await reconcileWatchersToReply(ccEmails, bccEmails);
      }

      setDraftHtml("");
      setDraftText("");
      setDraftFiles([]);
      setDraftAttachmentRefs([]);
      setNotifyAgentEmails([]);
      // Allow the subject-seed effect to re-fire after the ticket
      // detail refetches with the (possibly renamed) ticket.subject.
      // Without this, the seed key is unchanged so the input stays
      // showing the agent's just-sent edit; the agent reading the
      // next reply they're about to compose sees an in-progress
      // draft of someone else's subject.
      subjectSeededForRef.current = null;
      // Collapse the composer back to its resting state on success.
      // Without this, the agent who just clicked Send still sees the
      // draft form expanded (now with empty fields), which reads as
      // "did the send fail?". Skipped when at least one file upload
      // failed so the agent can see the error banner inline and
      // re-attach without re-typing the prose.
      if (uploadErrors.length === 0) {
        setComposerExpanded(false);
      }
      // Allow signature to reseed on the next public-reply expand
      // (matches FreshDesk: every fresh reply starts with the
      // signature regardless of whether the previous one used it).
      if (!isPrivate) signatureSeededForTicketRef.current = null;
      if (uploadErrors.length > 0) {
        setComposeError(
          `Sent, but some files failed: ${uploadErrors.join("; ")}`,
        );
      }
    } catch (e: unknown) {
      setComposeError(
        e instanceof Error ? e.message : "Failed to send.",
      );
    }
  }

  async function handleSaveEditComment(
    html: string,
    text: string,
    commentId: string,
    draftFields?: DraftSaveFields,
  ) {
    setEditError(null);
    try {
      await editComment.mutateAsync({
        commentId,
        body: { body_text: text, body_html: html || null, ...draftFields },
      });
      // Only collapse the parent-tracked edit state if it referred to
      // this comment. AI drafts have no parent editingCommentId (they
      // auto-edit), so we leave it null.
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
      }
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
        (e instanceof Error ? e.message : "Failed to save the edit.");
      setEditError(detail);
    }
  }

  // Promote an API-key draft into a public reply. Body comes from the
  // in-line editor on the bubble (the bubble owns the local edits, we
  // just pass them through to the mutation). On success the comment
  // flips is_private=false + sent_via_email=true, so the bubble
  // re-renders as a regular Public reply (no longer a draft).
  async function handleSendDraftComment(
    commentId: string,
    html: string,
    text: string,
    recipients?: {
      to_emails: string[];
      cc_emails: string[];
      bcc_emails: string[];
    },
    subject?: string,
  ) {
    setEditError(null);
    try {
      await sendDraftComment.mutateAsync({
        commentId,
        body: {
          body_text: text,
          body_html: html || null,
          ...(recipients
            ? {
                to_emails: recipients.to_emails,
                cc_emails: recipients.cc_emails,
                bcc_emails: recipients.bcc_emails,
              }
            : {}),
          ...(subject ? { subject } : {}),
        },
      });
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
        (e instanceof Error ? e.message : "Failed to send the reply.");
      setEditError(detail);
    }
  }

  async function handleConfirmDeleteComment() {
    if (!deleteCommentId) return;
    setDeleteCommentError(null);
    try {
      await deleteComment.mutateAsync(deleteCommentId);
      setDeleteCommentId(null);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
        (e instanceof Error ? e.message : "Failed to delete the note.");
      setDeleteCommentError(detail);
    }
  }

  function handleCancelCompose() {
    // Cancel = "abandon this whole composer". Clears BOTH tabs so the
    // agent can't accidentally ship a stale signature on the next open.
    setPublicDraftHtml("");
    setPublicDraftText("");
    setInternalDraftHtml("");
    setInternalDraftText("");
    setDraftFiles([]);
    setDraftAttachmentRefs([]);
    setNotifyAgentEmails([]);
    setComposeError(null);
    setComposerExpanded(false);
    // Allow the signature to reseed when the agent reopens the
    // composer (which they explicitly chose to cancel). Without this,
    // a one-shot ref would prevent reseed forever.
    signatureSeededForTicketRef.current = null;
  }

  /**
   * "Attach to Response" - the agent clicked the paperclip icon on an
   * earlier attachment in the conversation.
   *
   * An open AI draft wins: that is the reply the agent is about to
   * send, and before 2026-10-03 the only way to get a customer's file
   * onto it was download + re-upload. The draft row exists already, so
   * the file is cloned onto it now and shows in the draft's "Attached
   * to this reply" strip.
   *
   * With no open draft, stage it onto the manual composer if it's not
   * already there, expand the composer (it's the affordance that says
   * "yes, your click landed"), and switch to the Public Reply tab if
   * the agent was sitting on Internal Note - the common flow is "send
   * this file to the customer", not "stash a copy on a private note".
   */
  function handleAttachToResponse(attachment: AttachmentResponse) {
    if (openAiDraftId) {
      void attachExistingToDraft
        .mutateAsync({ commentId: openAiDraftId, attachmentId: attachment.id })
        .then((clone) => {
          // Same reason as handleUploadDraftFiles: show it now, not
          // after the next attachments poll.
          queryClient.setQueryData<AttachmentResponse[]>(
            agentTicketAttachmentsQueryKey(ticketNumber),
            (prev) => {
              const byId = new Map((prev ?? []).map((a) => [a.id, a] as const));
              byId.set(clone.id, clone);
              return Array.from(byId.values());
            },
          );
          // The draft can be a screen away from the tile that was
          // clicked; bring it into view so the click visibly landed.
          document
            .querySelector(`[data-comment-id="${openAiDraftId}"]`)
            ?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
        })
        .catch((e: unknown) => {
          const detail =
            (e as { response?: { data?: { detail?: string } } })?.response?.data
              ?.detail ?? (e instanceof Error ? e.message : "attach failed");
          setEditError(`Could not attach ${attachment.filename}: ${detail}`);
        });
      return;
    }
    setDraftAttachmentRefs((prev) => {
      if (prev.some((a) => a.id === attachment.id)) return prev;
      return [...prev, attachment];
    });
    setComposerExpanded(true);
    if (tab !== "public") setTab("public");
  }

  /**
   * Upload files straight onto an unsent AI draft comment. Errors are
   * collected per file, not thrown: one oversize file must not cost
   * the agent the rest of the batch.
   */
  async function handleUploadDraftFiles(
    commentId: string,
    files: File[],
  ): Promise<string[]> {
    const errors: string[] = [];
    const uploaded: AttachmentResponse[] = [];
    for (const f of files) {
      try {
        uploaded.push(await uploadAttachment.mutateAsync({ file: f, commentId }));
      } catch (e: unknown) {
        const detail =
          (e as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail ?? (e instanceof Error ? e.message : "upload failed");
        errors.push(`${f.name}: ${detail}`);
      }
    }
    // Write the new rows into the attachments cache so the strip shows
    // them now, not after the next poll. A file that vanishes for several
    // seconds reads as a failed upload and gets attached a second time.
    if (uploaded.length > 0) {
      await queryClient.cancelQueries({
        queryKey: agentTicketAttachmentsQueryKey(ticketNumber),
      });
      queryClient.setQueryData<AttachmentResponse[]>(
        agentTicketAttachmentsQueryKey(ticketNumber),
        (prev) => {
          const byId = new Map((prev ?? []).map((a) => [a.id, a] as const));
          for (const up of uploaded) byId.set(up.id, up);
          return Array.from(byId.values());
        },
      );
      void queryClient.invalidateQueries({
        queryKey: agentTicketAttachmentsQueryKey(ticketNumber),
      });
    }
    return errors;
  }

  function handleUnstageDraftAttachment(attachmentId: string) {
    setDraftAttachmentRefs((prev) => prev.filter((a) => a.id !== attachmentId));
  }

  function openRemoveAttachmentModal(attachment: AttachmentResponse) {
    setRemoveAttachmentError(null);
    setRemovingAttachment(attachment);
  }

  function closeRemoveAttachmentModal() {
    setRemovingAttachment(null);
    setRemoveAttachmentError(null);
  }

  async function handleConfirmRemoveAttachment() {
    if (!removingAttachment) return;
    setRemoveAttachmentError(null);
    try {
      await deleteAttachment.mutateAsync(removingAttachment.id);
      // Also unstage it from the draft if the agent had picked it via
      // Attach-to-Response: with the row gone the clone would 404 on
      // send.
      setDraftAttachmentRefs((prev) =>
        prev.filter((a) => a.id !== removingAttachment.id),
      );
      setRemovingAttachment(null);
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
        (e instanceof Error ? e.message : "Failed to remove the attachment.");
      setRemoveAttachmentError(detail);
    }
  }

  function openComposer(nextTab: ComposerTab = "public") {
    setTab(nextTab);
    setComposerExpanded(true);
    // Only scroll if the composer is NOT already visible. ``block:
    // "nearest"`` is the standard pattern: if the element is already
    // in the viewport, nothing happens; otherwise it scrolls the
    // minimum amount to bring it into view. This kills the SPEC §9
    // "jolt on Reply click" complaint - clicking the pill while the
    // composer is already on screen now leaves the scroll position
    // untouched. Deferred via rAF so we measure the element AFTER
    // React paints the expanded state.
    window.requestAnimationFrame(() => {
      composerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  }

  function handleForward() {
    if (!ticket) return;
    // Until a To: override ships on the composer, "Forward" prefills
    // the body with a quoted version of the original message. The
    // agent edits recipients via the CC/BCC editor inside the
    // composer. Public reply is forced because forwards go through
    // email.
    const created = (() => {
      const d = new Date(ticket.created_at);
      return Number.isNaN(d.getTime()) ? ticket.created_at : d.toLocaleString();
    })();
    const headerText = [
      "------ Forwarded message ------",
      `From: ${ticket.requester_display_name || ticket.requester_name} <${ticket.requester_email}>`,
      `Date: ${created}`,
      `Subject: ${ticket.subject}`,
      "",
      "",
    ].join("\n");
    const bodyText = ticket.description_text ?? "";
    // Write directly to publicDraft - forwards are always public
    // replies, AND ``setDraftText``/``setDraftHtml`` depend on the
    // ``tab`` closure that hasn't flipped yet (setTab below is async).
    // Using the explicit public setters avoids landing the quoted
    // forward body into an Internal Note draft when the agent had the
    // internal tab open before hitting Forward.
    setPublicDraftText(`${headerText}${bodyText}`);
    const headerHtml = headerText
      .split("\n")
      .map((line) => (line ? `<div>${line}</div>` : "<div><br/></div>"))
      .join("");
    const bodyHtml =
      ticket.description_html ??
      `<div>${(ticket.description_text ?? "")
        .split("\n")
        .map((line) => (line ? line : "<br/>"))
        .join("</div><div>")}</div>`;
    setPublicDraftHtml(`${headerHtml}${bodyHtml}`);
    openComposer("public");
  }

  async function handleCloseTicket() {
    if (!ticket || ticket.status === "closed") return;
    if (isClosing) return;
    setCloseError(null);
    setIsClosing(true);
    // Fire the PATCH and the 1500 ms minimum-hold in parallel. The
    // hold gives the agent visible feedback (spinner + "Closing..."
    // label) before the page navigates away, so the click registers
    // as "yes, that did something" rather than a silent route swap.
    // ``Promise.all`` returns once BOTH settle - the PATCH usually
    // completes well under 1500 ms in production, so the navigate
    // fires at the 1500 ms mark.
    try {
      await Promise.all([
        updateTicket.mutateAsync({ status: "closed" }),
        new Promise<void>((resolve) => setTimeout(resolve, 1500)),
      ]);
      navigate("/tickets");
    } catch (e: unknown) {
      setIsClosing(false);
      setCloseError(
        e instanceof Error ? e.message : "Failed to close the ticket.",
      );
    }
  }

  async function handleConfirmMarkSpam() {
    await markSpam.mutateAsync({ add_sender_block: true });
    setSpamOpen(false);
  }

  async function handleConfirmDelete() {
    setDeleteError(null);
    setIsDeleting(true);
    try {
      await softDeleteTicket.mutateAsync();
      setDeleteOpen(false);
      // Navigate to the ticket list so the agent has visible feedback
      // that the action took effect. Staying on the now-deleted
      // ticket page rendered as "nothing happened" - the default
      // detail fetch filters out deleted_at IS NOT NULL rows, so the
      // page would have flipped to 404 on the next refetch anyway.
      navigate("/tickets");
    } catch (e: unknown) {
      setDeleteError(
        e instanceof Error ? e.message : "Failed to delete the ticket.",
      );
    } finally {
      setIsDeleting(false);
    }
  }

  // Sticky ticket CC: after a public reply is sent, make the CC / BCC
  // watcher rows match what actually went out. A chip the agent added
  // becomes a watcher subscription (so it sticks to future replies); a
  // chip the agent removed becomes an unsubscribe (deleting the watcher)
  // - this is how "removing a Cc is permanent, not per-reply" is
  // enforced, which is what the 2026-05-20 change was reaching for. A
  // watcher who is now on the To line is never removed (they moved from
  // Cc to To, not off the ticket). Best-effort: the reply already sent,
  // so a failed watcher sync only affects the NEXT reply's seed, not the
  // send. Add/remove each invalidate the watcher query; the seed effect
  // is latched (seededForTicketRef) so it does not re-clobber the chips.
  async function reconcileWatchersToReply(
    cc: string[],
    bcc: string[],
  ): Promise<void> {
    const current = watchers.data ?? [];
    const toSet = new Set(toEmails.map((e) => e.toLowerCase()));
    const desiredCc = new Set(cc.map((e) => e.toLowerCase()));
    const desiredBcc = new Set(bcc.map((e) => e.toLowerCase()));
    const currentCc = current.filter((w) => w.kind === "cc");
    const currentBcc = current.filter((w) => w.kind === "bcc");
    const haveCc = new Set(currentCc.map((w) => w.email.toLowerCase()));
    const haveBcc = new Set(currentBcc.map((w) => w.email.toLowerCase()));

    const swallow = (p: Promise<unknown>): Promise<unknown> =>
      p.catch(() => undefined);
    const ops: Array<Promise<unknown>> = [];

    for (const e of desiredCc) {
      if (!haveCc.has(e)) {
        ops.push(
          swallow(addWatcher.mutateAsync({ kind: "cc", body: { email: e } })),
        );
      }
    }
    for (const e of desiredBcc) {
      if (!haveBcc.has(e)) {
        ops.push(
          swallow(addWatcher.mutateAsync({ kind: "bcc", body: { email: e } })),
        );
      }
    }
    for (const w of currentCc) {
      const e = w.email.toLowerCase();
      if (!desiredCc.has(e) && !toSet.has(e)) {
        ops.push(
          swallow(removeWatcher.mutateAsync({ kind: "cc", watcherId: w.id })),
        );
      }
    }
    for (const w of currentBcc) {
      const e = w.email.toLowerCase();
      if (!desiredBcc.has(e) && !toSet.has(e)) {
        ops.push(
          swallow(removeWatcher.mutateAsync({ kind: "bcc", watcherId: w.id })),
        );
      }
    }
    await Promise.all(ops);
  }

  // To / Cc / Bcc chip add/remove operate on composer-local state; the
  // watcher table is reconciled to the sent Cc / Bcc on send (see
  // reconcileWatchersToReply). Adds are deduped + lowercased.
  function normalize(email: string): string {
    return email.trim().toLowerCase();
  }

  function handleAddTo(email: string) {
    const n = normalize(email);
    if (!n) return;
    setToEmails((prev) => (prev.includes(n) ? prev : [...prev, n]));
  }

  function handleAddCc(email: string) {
    const n = normalize(email);
    if (!n) return;
    setCcEmails((prev) => (prev.includes(n) ? prev : [...prev, n]));
  }

  function handleAddBcc(email: string) {
    const n = normalize(email);
    if (!n) return;
    setBccEmails((prev) => (prev.includes(n) ? prev : [...prev, n]));
  }

  function handleRemoveTo(email: string) {
    setToEmails((prev) => prev.filter((e) => e !== email));
  }

  function handleRemoveCc(email: string) {
    setCcEmails((prev) => prev.filter((e) => e !== email));
  }

  function handleClearCc() {
    setCcEmails([]);
  }

  function handleRemoveBcc(email: string) {
    setBccEmails((prev) => prev.filter((e) => e !== email));
  }

  return (
    // Full-width container. New layout (May 2026):
    //   - Compact meta strip at the top: TICKET-# / requester / company / subject + actions.
    //   - Composer is the FIRST thing in the main column so the most
    //     common agent action is in the top fold.
    //   - Conversation thread sits below the composer, newest reply at
    //     the top, with the original ticket (description + attachments)
    //     rendered as the bottom-most "Original ticket" entry. This
    //     lets the thread read newest-to-oldest top-to-bottom with the
    //     root of the conversation at the natural anchor point.
    //   - Right rail holds the ticket's mutable Properties card, then
    //     the audit timeline, then the related-tickets rail. The
    //     properties card is what was previously a top-of-page card -
    //     out of the way of the agent's primary task (replying) but
    //     still one click away.
    //   - CC / BCC editors only render inside the composer (Public
    //     Reply tab). Internal notes don't send email so the fields
    //     are hidden when the Internal Note tab is active.
    // `pb-28` on mobile reserves room for the fixed Properties dock so
    // the docked bar never covers the last bit of scrolled content;
    // reset to `lg:pb-0` on desktop where there is no dock.
    <div className="w-full space-y-4 pb-28 lg:pb-0">
      {/* Post-merge notice. Set when a tombstone redirected here or a
          merge just ran from this page; dismissible and not persisted -
          the durable record is the audit timeline's ticket_merged_from
          entry. */}
      {mergeNotice && (
        <Alert variant="info" data-testid="merged-from-notice">
          <span>{mergeNotice}</span>
          <button
            type="button"
            onClick={() => setMergeNotice(null)}
            className="ml-3 text-sm font-medium underline hover:no-underline"
          >
            Dismiss
          </button>
        </Alert>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <nav aria-label="Breadcrumb" className="text-xs text-neutral-500">
          <Link to="/tickets" className="hover:text-neutral-700">
            Tickets
          </Link>
          <span className="mx-2 text-neutral-300">/</span>
          <span
            className="text-neutral-700"
            title={`Internal ticket_number #${ticket.ticket_number}`}
          >
            #{ticket.public_code}
          </span>
        </nav>

        {/* Global ticket search - searches all tickets from anywhere,
            navigating to the inbox with the query running. Mirrors the
            tickets-page search box. */}
        <form
          onSubmit={submitHeaderSearch}
          className="relative w-full shrink-0 sm:w-72"
          role="search"
        >
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
            <i
              className="fa-duotone fa-thin fa-magnifying-glass text-sm"
              aria-hidden="true"
            />
          </span>
          <input
            type="search"
            placeholder="Search all tickets..."
            value={headerSearch}
            onChange={(e) => setHeaderSearch(e.target.value)}
            aria-label="Search all tickets"
            className="block w-full rounded-lg border-neutral-300 py-2 pl-9 pr-9 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
          {headerSearch.length > 0 && (
            <button
              type="button"
              onClick={() => setHeaderSearch("")}
              aria-label="Clear search"
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 hover:text-neutral-700"
            >
              <i
                className="fa-duotone fa-thin fa-xmark text-sm"
                aria-hidden="true"
              />
            </button>
          )}
        </form>
      </div>

      {/* Prominent header: subject is the visual anchor (page title),
          status + priority badges sit inline on the same line so the
          ticket's state reads at a glance. The TICKET-# / requester /
          company line drops to a smaller meta row below. The action
          bar groups non-destructive actions (Reply, Forward, Close)
          on the left of a divider and destructive actions (Mark
          spam, Delete) on the right. */}
      <Card padding="sm" data-testid="ticket-detail-header">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <EditableTicketSubject
                subject={ticket.subject}
                onSave={handleSaveSubject}
              />
              <StatusBadge status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-500">
              {/* Customer-facing short code (e.g. "JK4" or "F-12345"),
                  styled mono so it reads as an identifier. The legacy
                  internal ticket_number is preserved in the title
                  attribute for forensic lookups. */}
              <span
                className="font-mono font-medium text-neutral-600"
                title={`Internal ticket_number #${ticket.ticket_number}`}
              >
                #{ticket.public_code}
              </span>
              <span className="text-neutral-300" aria-hidden="true">·</span>
              <span className="text-neutral-700">
                {ticket.requester_display_name || ticket.requester_name}
              </span>
              <span className="text-neutral-400">
                &lt;{ticket.requester_email}&gt;
              </span>
              {ticket.created_at && (
                <>
                  <span className="text-neutral-300" aria-hidden="true">·</span>
                  <span
                    className="text-neutral-500"
                    title={`Created ${fmtTimestamp(ticket.created_at)}`}
                  >
                    Created {fmtDate(ticket.created_at)}
                  </span>
                </>
              )}
              {ticket.company_name && (
                <>
                  <span className="text-neutral-300" aria-hidden="true">·</span>
                  <span>{ticket.company_name}</span>
                </>
              )}
              {forwardedByEvent && (
                <span
                  className="ml-1 inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-0.5 text-[0.6875rem] font-medium text-violet-700"
                  data-testid="agent-forward-badge"
                  title={
                    typeof forwardedByEvent.event_metadata
                      ?.parsed_customer_email === "string"
                      ? `Customer parsed from body: ${forwardedByEvent.event_metadata.parsed_customer_email}`
                      : undefined
                  }
                >
                  <i
                    className="fa-duotone fa-thin fa-share-from-square"
                    aria-hidden="true"
                  />
                  Forwarded by{" "}
                  {forwardedByEvent.actor_name ||
                    (typeof forwardedByEvent.event_metadata
                      ?.forwarding_agent_email === "string"
                      ? forwardedByEvent.event_metadata.forwarding_agent_email
                      : null) ||
                    forwardedByEvent.actor_email ||
                    "(agent)"}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => openComposer("public")}
              data-testid="header-action-reply"
            >
              <i
                className="fa-duotone fa-thin fa-reply mr-1.5"
                aria-hidden="true"
              />
              Reply
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleForward}
              data-testid="header-action-forward"
            >
              <i
                className="fa-duotone fa-thin fa-share mr-1.5"
                aria-hidden="true"
              />
              Forward
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                void handleCloseTicket();
              }}
              disabled={
                ticket.status === "closed" ||
                updateTicket.isPending ||
                isClosing
              }
              isLoading={isClosing}
              data-testid="header-action-close"
            >
              {/* Static check icon is suppressed while ``isLoading``
                  fires so the spinner provided by Button is the only
                  visual signal. Label flips to "Closing..." so a
                  screen-reader user gets the same progress feedback
                  as a sighted one. */}
              {!isClosing && (
                <i
                  className="fa-duotone fa-thin fa-circle-check mr-1.5"
                  aria-hidden="true"
                />
              )}
              {isClosing ? "Closing..." : "Close"}
            </Button>
            {canMerge && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setMergeOpen(true)}
                data-testid="header-action-merge"
              >
                <i
                  className="fa-duotone fa-thin fa-merge mr-1.5"
                  aria-hidden="true"
                />
                Merge
              </Button>
            )}
            {(canMarkSpam || currentUser?.isSystemAdmin) && (
              <span
                aria-hidden="true"
                className="mx-1 hidden h-5 w-px bg-neutral-200 sm:inline-block"
              />
            )}
            {canMarkSpam && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setSpamOpen(true)}
              >
                Mark spam
              </Button>
            )}
            {currentUser?.isSystemAdmin && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setDeleteOpen(true)}
              >
                Delete
              </Button>
            )}
          </div>
        </div>
        {closeError && (
          <div className="mt-3">
            <Alert variant="error">{closeError}</Alert>
          </div>
        )}
      </Card>

      {/* Current Status - pinned above the fold, full width, on every
          ticket whether or not it has been written yet. This is the
          first thing Greg reads when he opens a ticket, so nothing
          goes above it except the ticket's own header. */}
      <CurrentStatusCard
        html={ticket.current_status_html}
        updatedAt={ticket.current_status_updated_at}
        isSaving={updateTicket.isPending && currentStatusSaving}
        saveError={currentStatusError}
        onSave={handleSaveCurrentStatus}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Main column - composer at the top, then conversation thread */}
        <div className="space-y-4 lg:col-span-2">
          {/* Composer (top of the fold). Collapsed by default - the
              full editor only mounts after the agent clicks into the
              pill, hits Reply/Forward in the header, or returns to a
              ticket with an in-progress draft. */}
          <Card>
            <div ref={composerRef} />
            <div
              role="tablist"
              aria-label="Composer mode"
              className="mb-3 inline-flex rounded-lg border border-neutral-200 bg-neutral-50 p-1"
            >
              <button
                type="button"
                role="tab"
                aria-selected={tab === "public"}
                onClick={() => {
                  setTab("public");
                  setComposerExpanded(true);
                }}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  tab === "public"
                    ? "bg-white text-primary-700 shadow-sm"
                    : "text-neutral-600 hover:text-neutral-800"
                }`}
              >
                Public Reply
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "internal"}
                onClick={() => {
                  setTab("internal");
                  setComposerExpanded(true);
                }}
                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                  tab === "internal"
                    ? "bg-amber-100 text-amber-800 shadow-sm"
                    : "text-neutral-600 hover:text-neutral-800"
                }`}
                data-testid="composer-tab-internal"
              >
                Internal Note
              </button>
            </div>

            {!isComposerOpen && (
              <button
                type="button"
                onClick={() => openComposer(tab)}
                data-testid="composer-collapsed"
                className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                  tab === "internal"
                    ? "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
                    : "border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300 hover:bg-neutral-50"
                }`}
                aria-label={
                  tab === "public"
                    ? "Open reply composer"
                    : "Open internal note composer"
                }
              >
                <i
                  className={`fa-duotone fa-thin ${
                    tab === "public" ? "fa-reply" : "fa-note-sticky"
                  }`}
                  aria-hidden="true"
                />
                <span>
                  {tab === "public"
                    ? "Type your reply..."
                    : "Type an internal note..."}
                </span>
              </button>
            )}

            <div
              className={`space-y-3 rounded-lg p-3 ${
                isComposerOpen ? "" : "hidden"
              } ${tab === "internal" ? "bg-amber-50" : "bg-white"}`}
              data-testid="composer-container"
              data-tab={tab}
              data-expanded={isComposerOpen ? "true" : "false"}
            >
              {/* Recipient header sits ABOVE the body, matching what
                  every email client does. Only renders on the Public
                  Reply tab - internal notes don't send email so
                  From/To/Cc/Bcc aren't applicable. */}
              {tab === "public" && (
                <RecipientHeader
                  ticketNumber={ticket.ticket_number}
                  // Pull the live outbound mailbox + display name from
                  // /agent/reply-signature so the From: row reflects
                  // whatever ``MS_GRAPH_MAILBOX`` is configured to
                  // (e.g. ``btest@buyist.com`` during testing,
                  // ``support@buyist.com`` post-cutover). Without this
                  // the row would render the React default of
                  // support@buyist.com, which lies to the agent about
                  // where the recipient will actually see the reply
                  // coming from. Omitting the props (signature still
                  // loading) lets RecipientHeader fall back to its
                  // own defaults so first paint stays clean.
                  fromEmail={signatureQuery.data?.from_email}
                  fromName={signatureQuery.data?.from_name ?? undefined}
                  requesterEmail={ticket.requester_email}
                  requesterName={
                    ticket.requester_display_name || ticket.requester_name
                  }
                  toEmails={toEmails}
                  ccEmails={ccEmails}
                  bccEmails={bccEmails}
                  onAddTo={handleAddTo}
                  onRemoveTo={handleRemoveTo}
                  onAddCc={handleAddCc}
                  onRemoveCc={handleRemoveCc}
                  onClearCc={handleClearCc}
                  onAddBcc={handleAddBcc}
                  onRemoveBcc={handleRemoveBcc}
                  subject={publicSubjectDraft}
                  onChangeSubject={setPublicSubjectDraft}
                />
              )}
              {/* Internal Note "To" - agents to email with the note.
                  FreshDesk parity: like a public reply's To, but agents
                  only. Notifying an agent does NOT reassign the ticket. */}
              {tab === "internal" && (
                <AgentNotifyPicker
                  selectedEmails={notifyAgentEmails}
                  onChange={setNotifyAgentEmails}
                  disabled={addComment.isPending}
                />
              )}
              <RichTextEditor
                value={draftHtml}
                onChange={(html, text) => {
                  setDraftHtml(html);
                  setDraftText(text);
                }}
                minHeight="24rem"
                placeholder={
                  tab === "public"
                    ? "Type your public reply..."
                    : "Type an internal note..."
                }
                ariaLabel={
                  tab === "public" ? "Public reply body" : "Internal note body"
                }
                // Pasted screenshots upload to /inline-images and are
                // inserted as <img src=URL> ResizableImage nodes. The
                // inline-image flow is agent-only.
                onUploadImage={uploadInlineImage}
              />
              <AttachmentDropzone
                value={draftFiles}
                onChange={setDraftFiles}
                compact
              />
              {/* "Attach to Response" staging strip. Lists files the
                  agent picked from earlier in the conversation - shows
                  which files will ride along on send, lets the agent
                  unstage any of them, and visually separates them
                  from freshly-uploaded files in the dropzone above. */}
              {draftAttachmentRefs.length > 0 && (
                <div
                  data-testid="staged-attachments"
                  className="flex flex-wrap items-center gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900"
                >
                  <span className="font-medium uppercase tracking-wide">
                    Re-attaching:
                  </span>
                  {draftAttachmentRefs.map((a) => (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-xs text-neutral-800 shadow-sm"
                    >
                      <i
                        className="fa-duotone fa-thin fa-paperclip text-neutral-500"
                        aria-hidden="true"
                      />
                      <span className="max-w-[16rem] truncate">
                        {a.filename}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleUnstageDraftAttachment(a.id)}
                        title="Unstage this attachment"
                        aria-label={`Unstage ${a.filename}`}
                        className="text-neutral-400 hover:text-red-600"
                      >
                        <i
                          className="fa-duotone fa-thin fa-xmark text-sm"
                          aria-hidden="true"
                        />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {composeError && <Alert variant="error">{composeError}</Alert>}
              <div className="flex flex-wrap items-center gap-2">
                {tab === "public" ? (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => {
                      void handlePostComment();
                    }}
                    isLoading={addComment.isPending}
                  >
                    Send Reply
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    className="bg-amber-600 hover:bg-amber-700 active:bg-amber-800"
                    onClick={() => {
                      void handlePostComment();
                    }}
                    isLoading={addComment.isPending}
                  >
                    Add Note
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelCompose}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>

          {/* Conversation - newest reply at the top, original at the
              bottom as the natural root of the thread. Attachments are
              rendered in the reply / note they belong to (the importer
              preserves FD's conversation_id linkage); only attachments
              with comment_id=null fall to the bottom "Original ticket"
              section, which is the right place for files attached to
              the new-ticket form itself. */}
          <Card>
            <h2 className="text-sm font-semibold text-neutral-700">
              Conversation ({conversationCount})
            </h2>
            <div className="mt-4 space-y-3">
              {comments.isLoading && (
                <div className="flex items-center justify-center py-6">
                  <Spinner />
                </div>
              )}
              {(() => {
                // All three of these are memoized at the top of the
                // component (see ``attachmentsByComment`` /
                // ``renderedComments``) so a keystroke in the composer
                // does not re-group the attachments or re-sort the
                // thread. Aliased here to keep the JSX below unchanged.
                const byComment = attachmentsByComment;
                const ticketLevel = ticketLevelAttachments;
                const sorted = renderedComments;

                return (
                  <>
                    {sorted.map((c) => (
                      <AgentCommentBubble
                        key={c.id}
                        comment={c}
                        defaultCollapsedMobile={c.id !== latestCustomerCommentId}
                        attachments={byComment.get(c.id) ?? []}
                        ticketNumber={ticket.ticket_number}
                        ticketPublicCode={ticket.public_code}
                        ticketSubject={ticket.subject ?? ""}
                        currentUserId={currentUser?.id ?? null}
                        isSystemAdmin={currentUser?.isSystemAdmin === true}
                        requesterEmail={ticket.requester_email}
                        requesterName={
                          ticket.requester_display_name ||
                          ticket.requester_name ||
                          ""
                        }
                        fromEmail={signatureQuery.data?.from_email}
                        fromName={signatureQuery.data?.from_name}
                        onEdit={(id) => {
                          setEditError(null);
                          setEditingCommentId(id);
                        }}
                        onDelete={(id) => {
                          setDeleteCommentError(null);
                          setDeleteCommentId(id);
                        }}
                        isEditing={editingCommentId === c.id}
                        // Keyed on the mutation's own commentId, not
                        // editingCommentId: an AI draft auto-edits with no
                        // parent edit state, so Save Draft never showed
                        // it was working.
                        isSaving={
                          editComment.isPending &&
                          editComment.variables?.commentId === c.id
                        }
                        onSaveEdit={(html, text, commentId, draftFields) =>
                          handleSaveEditComment(html, text, commentId, draftFields)
                        }
                        onCancelEdit={() => {
                          setEditingCommentId(null);
                          setEditError(null);
                        }}
                        onSendDraft={(commentId, html, text, recipients, subject) =>
                          handleSendDraftComment(commentId, html, text, recipients, subject)
                        }
                        isSendingDraft={
                          sendDraftComment.isPending &&
                          sendDraftComment.variables?.commentId === c.id
                        }
                        isDeletingThis={
                          deleteComment.isPending && deleteCommentId === c.id
                        }
                        onAttachToResponse={handleAttachToResponse}
                        onRemoveAttachment={openRemoveAttachmentModal}
                        onUploadDraftFiles={handleUploadDraftFiles}
                        onUploadInlineImage={uploadInlineImage}
                        attachedToDraftIds={attachedToDraftIds}
                        onSplitFrom={canMerge ? setSplitStartId : undefined}
                        onMove={canMerge ? setMoveComment : undefined}
                      />
                    ))}
                    {/* Surfaces save-edit / send-draft / save-draft
                        errors. Both flows reuse setEditError so a
                        single Alert covers them. */}
                    {editError && (
                      <Alert variant="error">{editError}</Alert>
                    )}

                    {/* The rest of the thread, in the position it would
                        occupy chronologically (this list is newest
                        first, so older messages belong under it and
                        above the original). Nothing behind this button
                        has been downloaded: the server was told to load
                        only the newest bodies, so clicking it is a real
                        fetch, not a CSS reveal. */}
                    {omittedCount > 0 && (
                      <button
                        type="button"
                        data-testid="load-remaining-history"
                        onClick={() => {
                          // Already expanded but still short: the fetch
                          // failed. Re-running it is the retry, since
                          // flipping an already-true flag would do
                          // nothing at all.
                          if (historyExpanded) {
                            void comments.refetch();
                            return;
                          }
                          setHistoryExpanded(true);
                        }}
                        // Only inert while THIS request is in flight.
                        // The thread also polls every 15 s; disabling on
                        // any fetch would make the button reject clicks
                        // that happen to land on a poll tick.
                        disabled={historyExpanded && comments.isFetching}
                        className="w-full rounded-lg border border-dashed border-neutral-300 bg-white px-4 py-3 text-sm font-medium text-primary-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50 disabled:cursor-progress disabled:text-neutral-500"
                      >
                        {historyExpanded && comments.isFetching ? (
                          <>
                            <Spinner />
                            <span className="ml-2">Loading history...</span>
                          </>
                        ) : (
                          <>
                            <i
                              className="fa-duotone fa-thin fa-clock-rotate-left mr-2"
                              aria-hidden="true"
                            />
                            Load Remaining History ({omittedCount} older{" "}
                            {omittedCount === 1 ? "message" : "messages"})
                          </>
                        )}
                      </button>
                    )}

                    {/* Original ticket as the bottom-most entry. Only
                        attachments without a comment_id appear here -
                        per-comment files render in their own bubble
                        above. */}
                    <div
                      data-testid="original-ticket-entry"
                      // Same click-anywhere-to-open rule as the comment
                      // bubbles above. Mobile-only, like its collapse.
                      onClick={(e) => {
                        if (originalOpenMobile) return;
                        if (
                          (e.target as HTMLElement).closest(
                            "button, a, input, select",
                          )
                        ) {
                          return;
                        }
                        setOriginalOpenMobile(true);
                      }}
                      className={`mt-4 rounded-lg border border-neutral-200 bg-neutral-50 p-4 ${
                        originalOpenMobile
                          ? ""
                          : "cursor-pointer lg:cursor-default"
                      }`}
                    >
                      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                        <button
                          type="button"
                          onClick={() => setOriginalOpenMobile((v) => !v)}
                          className="lg:hidden -ml-1 rounded p-1 text-neutral-500 hover:bg-neutral-200/60"
                          aria-expanded={originalOpenMobile}
                          aria-label={
                            originalOpenMobile
                              ? "Collapse original ticket"
                              : "Expand original ticket"
                          }
                          data-testid="original-ticket-toggle"
                        >
                          <i
                            className={`fa-duotone fa-thin ${
                              originalOpenMobile
                                ? "fa-chevron-down"
                                : "fa-chevron-right"
                            }`}
                            aria-hidden="true"
                          />
                        </button>
                        <span className="rounded-full bg-neutral-200 px-2 py-0.5 font-medium uppercase tracking-wide text-neutral-700">
                          Original ticket
                        </span>
                        <span className="text-neutral-500">
                          from{" "}
                          {ticket.requester_display_name ||
                            ticket.requester_name}
                        </span>
                        <span className="text-neutral-400">·</span>
                        <span className="text-neutral-500">
                          {ticket.requester_email}
                        </span>
                        <span className="text-neutral-400">·</span>
                        <span
                          className="text-neutral-500"
                          title={new Date(ticket.created_at).toString()}
                        >
                          sent {fmtTimestamp(ticket.created_at)}
                        </span>
                      </div>
                      {/* Mobile collapse for the original-ticket body.
                          Always shown on desktop (`lg:block`). */}
                      <div
                        className={
                          originalOpenMobile ? "block" : "hidden lg:block"
                        }
                      >
                      {(() => {
                        // Show the original CC list (or the current
                        // watcher CCs) so the agent can see who else
                        // was on the email. FD-imported tickets get
                        // these from tickets.cc_emails at import time;
                        // native tickets accumulate them as agents
                        // add CCs in the composer.
                        const ccs = (watchers.data ?? []).filter(
                          (w) => w.kind === "cc",
                        );
                        if (ccs.length === 0) return null;
                        return (
                          <div
                            data-testid="original-ticket-cc"
                            className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-neutral-500"
                          >
                            <span className="font-medium text-neutral-600">
                              cc
                            </span>
                            {ccs.map((w, idx) => (
                              <span key={w.id}>
                                {w.email}
                                {idx < ccs.length - 1 ? "," : ""}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                      <div className="prose prose-sm max-w-none text-sm text-neutral-800">
                        {ticket.description_html ? (
                          <div
                            dangerouslySetInnerHTML={descriptionHtmlProps}
                          />
                        ) : (
                          <div className="whitespace-pre-wrap">
                            {ticket.description_text}
                          </div>
                        )}
                      </div>
                      {ticketLevel.length > 0 && (
                        <div
                          data-testid="original-ticket-attachments"
                          className="mt-3 border-t border-neutral-200 pt-3"
                        >
                          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                            Attachments
                          </h3>
                          <div className="flex flex-wrap gap-3">
                            {ticketLevel.map((a) => (
                              <AttachmentTile
                                key={a.id}
                                attachment={a}
                                ticketNumber={ticket.ticket_number}
                                onAttachToResponse={handleAttachToResponse}
                                onRemove={openRemoveAttachmentModal}
                                isAttachedToDraft={attachedToDraftIds.has(a.id)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </Card>
        </div>

        {/* Right rail: status header, properties, audit, related.
            ``lg:sticky`` docks the rail to the top of the scroll
            container on desktop so an agent reading a long conversation
            keeps Status / Properties / AuditTimeline / Recent in view
            without scrolling back up. ``lg:self-start`` is required
            because the grid otherwise stretches the rail to the row's
            full height, which neuters sticky positioning. The
            ``max-h``/``overflow-y-auto`` pair lets the rail itself
            scroll when its own content (an unusually long audit
            timeline) exceeds the viewport. Mobile keeps the natural
            single-column stacked flow. */}
        <div className="space-y-4 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto">
          {/* At-a-glance status header. The detail screen historically
              made an agent hunt through the Properties form to see
              the ticket's state. Reading "Open" / "Closed" at the
              very top of the rail mirrors Freshdesk and keeps Status
              one click away (the Properties select below still does
              the editing). */}
          {/* Redundant on mobile - the docked Properties peek already
              shows Status there - so this rail card is desktop-only. */}
          <Card
            padding="sm"
            data-testid="rail-status-header"
            className="hidden lg:block"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                  Status
                </div>
                <div className="mt-1 text-lg font-semibold leading-tight text-neutral-900">
                  {STATUS_OPTIONS.find((o) => o.value === ticket.status)
                    ?.label ?? ticket.status}
                </div>
              </div>
              <StatusBadge status={ticket.status} />
            </div>
          </Card>

          {/* Properties. ONE instance of the form, repositioned by CSS:
              a docked, expandable bottom sheet on mobile (< lg); the
              normal right-rail card on desktop. We do not render it
              twice because the pickers carry hardcoded test ids and
              duplicate DOM ids would break. The outer shell is `fixed`
              on phones and reset to `lg:static` (transparent, no
              border/bg/shadow) on desktop so the rail looks unchanged.
              z-20 keeps it under the AppShell nav drawer (z-40) and its
              overlay (z-30). */}
          <div
            data-testid="mobile-properties-dock"
            // Tinted, taller, heavier top edge on mobile (Greg,
            // 2026-07-30): white-on-white made it read as the end of
            // the page rather than as a control. Matches the inbox's
            // Filters dock so the two bottom bars behave alike. All of
            // it resets at `lg:` - the desktop rail is unchanged.
            className="fixed inset-x-0 bottom-0 z-20 border-t-2 border-primary-500 bg-primary-50 shadow-[0_-4px_14px_rgba(0,0,0,0.12)] lg:static lg:z-auto lg:border-0 lg:bg-transparent lg:shadow-none"
          >
            {/* Peek bar - mobile only. Shows Status + assignee at a
                glance and toggles the full form. */}
            <button
              type="button"
              onClick={() => setDockOpen((v) => !v)}
              aria-expanded={dockOpen}
              aria-controls="ticket-properties-body"
              data-testid="mobile-properties-peek"
              className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left lg:hidden"
            >
              <span className="flex items-center gap-2 text-base">
                <span className="font-semibold text-primary-800">
                  Properties
                </span>
                <StatusBadge status={ticket.status} />
                <span className="text-sm text-primary-700">
                  {assigneeValue === "unassigned"
                    ? "Unassigned"
                    : assigneeValue === "mine"
                      ? "Me"
                      : "Assigned"}
                </span>
              </span>
              <i
                className={`fa-duotone fa-thin ${
                  dockOpen ? "fa-chevron-down" : "fa-chevron-up"
                } text-lg text-primary-600`}
                aria-hidden="true"
              />
            </button>
            <div
              id="ticket-properties-body"
              className={`${
                dockOpen ? "block" : "hidden"
              } max-h-[70vh] overflow-y-auto px-4 pb-4 lg:block lg:max-h-none lg:overflow-visible lg:p-0`}
            >
          <Card>
            <h2 className="text-sm font-semibold text-neutral-700">
              Properties
            </h2>
            {/* Read-only merge provenance (2026-07-15). Which tickets
                were folded into this one; each still routes here, so
                the line doubles as the list of aliases this ticket
                answers to. */}
            {(ticket.merged_from_codes?.length ?? 0) > 0 && (
              <p
                className="mt-2 text-xs text-neutral-500"
                data-testid="merged-from-line"
              >
                <i
                  className="fa-duotone fa-thin fa-merge mr-1"
                  aria-hidden="true"
                />
                Merged from{" "}
                {(ticket.merged_from_codes ?? [])
                  .map((c) => `[#${c}]`)
                  .join(", ")}
              </p>
            )}
            <div className="mt-3 space-y-3">
              {/* Each control wraps in a span tagged with
                  ``data-just-saved`` so the index.css keyframe pulses
                  the dropdown after a successful PATCH. The wrapper
                  carries the attribute (rather than the inner select)
                  because AssigneePicker and the other pickers render
                  their own composite controls and we want a
                  consistent target. */}
              {/* Ticket Owner picker - the customer this ticket belongs
                  to. Sits ABOVE Status per Greg's 2026-05-17 request so
                  the most-edited identity field reads as the headline
                  of the Properties card. */}
              <span
                data-just-saved={recentlySavedField === "owner" ? "true" : "false"}
                className="block"
              >
                <TicketOwnerPicker
                  id="owner-edit"
                  label="Ticket Owner"
                  ticketNumber={ticket.ticket_number}
                  value={{
                    id: ticket.requester_user_id,
                    email: ticket.requester_email,
                    name:
                      ticket.requester_display_name ||
                      ticket.requester_name,
                  }}
                  onChange={(next) => {
                    void handleOwner(next);
                  }}
                />
              </span>
              <span
                data-just-saved={recentlySavedField === "status" ? "true" : "false"}
                className="block"
              >
                <Select
                  id="status-edit"
                  label="Status"
                  options={STATUS_OPTIONS}
                  value={ticket.status}
                  onChange={(e) => {
                    void handleStatus(e.target.value as TicketStatus);
                  }}
                />
              </span>
              <span
                data-just-saved={recentlySavedField === "priority" ? "true" : "false"}
                className="block"
              >
                <Select
                  id="priority-edit"
                  label="Priority"
                  options={PRIORITY_OPTIONS}
                  value={ticket.priority}
                  onChange={(e) => {
                    void handlePriority(e.target.value as TicketPriority);
                  }}
                />
              </span>
              <span
                data-just-saved={recentlySavedField === "type" ? "true" : "false"}
                className="block"
              >
                <Select
                  id="type-edit"
                  label="Type"
                  options={TYPE_OPTIONS}
                  value={ticket.type ?? ""}
                  onChange={(e) => {
                    void handleType(e.target.value);
                  }}
                />
              </span>
              <span
                data-just-saved={recentlySavedField === "assignee" ? "true" : "false"}
                className="block"
              >
                <AssigneePicker
                  id="assignee-edit"
                  label="Assignee"
                  value={assigneeValue}
                  onChange={(v) => {
                    void handleAssignee(v);
                  }}
                />
              </span>
              {/* Tags editor (Greg 2026-07-13). Plain span, no
                  data-just-saved: tags do not ride the PATCH path, and
                  chips appearing or disappearing is already the save
                  feedback. The Brand picker that used to sit below was
                  removed 2026-08-08 per Greg ("we don't use it"); the
                  brand_id column and the requester-card brand display
                  are untouched. */}
              <span className="block">
                <TagsEditor ticketNumber={ticket.ticket_number} />
              </span>
              {/* Remind Me (plan 2026-08-08 A14): date + day-part set
                  control, or the pending reminder with an x to clear.
                  Plain span, no data-just-saved: the control swaps to
                  its pending state on success, which is already the
                  save feedback (same reasoning as TagsEditor above). */}
              <span className="block">
                <ReminderControl ticketNumber={ticket.ticket_number} />
              </span>
            </div>
          </Card>
            </div>
          </div>

          {/* Documents - every (non-inline) file on the ticket, plus the
              AI-readable manifest. Collapsed by default. Sits directly
              under Properties per Greg's 2026-06-07 request. */}
          <DocumentsCard
            ticketNumber={ticket.ticket_number}
            attachments={attachments.data ?? []}
            onAttachToResponse={handleAttachToResponse}
            onRemove={openRemoveAttachmentModal}
            attachedDraftIds={attachedToDraftIds}
          />

          <Card>
            <AuditTimeline
              ticketId={ticket.id}
              ticketNumber={ticket.ticket_number}
              onRequestCommentReveal={handleRequestCommentReveal}
            />
          </Card>

          {/* Recently viewed tickets - hidden on small viewports inside
              the component itself to keep the detail page tight on
              phones. Replaces the earlier "Related tickets" rail. */}
          <RecentlyViewedTicketsRail
            currentTicketNumber={ticket.ticket_number}
          />
        </div>
      </div>

      {/* Merge modal. On success: if this ticket was folded into a
          master, navigate there (the tombstone would redirect anyway -
          going directly avoids the intermediate fetch); if this ticket
          IS the master, stay put and announce inline. */}
      <MergeTicketModal
        isOpen={mergeOpen}
        onClose={() => setMergeOpen(false)}
        ticket={ticket}
        onMerged={(masterPublicCode, direction, sourcePublicCodes) => {
          setMergeOpen(false);
          if (direction === "into") {
            navigate(`/tickets/${masterPublicCode.toLowerCase()}`, {
              replace: true,
              state: { mergedFrom: ticket.public_code },
            });
          } else {
            const folded = sourcePublicCodes
              .map((c) => `[#${c}]`)
              .join(", ");
            setMergeNotice(
              `Merged ${folded} into this ticket. Replies to ` +
                `${sourcePublicCodes.length === 1 ? "it" : "them"} now land here.`,
            );
          }
        }}
      />

      {/* Split modal. On success go to the new ticket: that is where
          the agent's next reply belongs. */}
      <SplitTicketModal
        startCommentId={splitStartId}
        onClose={() => setSplitStartId(null)}
        ticket={ticket}
        comments={comments.data ?? []}
        onSplit={(newPublicCode) => {
          setSplitStartId(null);
          navigate(`/tickets/${newPublicCode.toLowerCase()}`);
        }}
      />

      {/* Move modal. The agent stays on this ticket (they are usually
          triaging it); the notice says where the message went. */}
      <MoveCommentModal
        comment={moveComment}
        onClose={() => setMoveComment(null)}
        ticket={ticket}
        onMoved={(targetPublicCode, count) => {
          setMoveComment(null);
          setMergeNotice(
            `Moved ${count} message${count === 1 ? "" : "s"} to [#${targetPublicCode}]. ` +
              "Replies to a moved email now go there.",
          );
        }}
      />

      {/* Mark spam modal */}
      <Modal
        isOpen={spamOpen}
        onClose={() => setSpamOpen(false)}
        title="Mark ticket as spam?"
      >
        <p className="text-sm text-neutral-700">
          This will mark the ticket as spam AND add the sender to the
          blocklist. Future emails from this sender will be auto-marked.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setSpamOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            className="bg-red-600 hover:bg-red-700 active:bg-red-800"
            onClick={() => {
              void handleConfirmMarkSpam();
            }}
            isLoading={markSpam.isPending}
          >
            Mark Spam
          </Button>
        </div>
      </Modal>

      {/* Delete-note modal. Triggered from the AgentCommentBubble's
          trash icon. Backend cascades attachments out of DB + best-
          effort cleans up Spaces; the audit trail captures the
          deletion as a ticket_event so the action is recoverable. */}
      <Modal
        isOpen={deleteCommentId !== null}
        onClose={() => {
          if (!deleteComment.isPending) {
            setDeleteCommentId(null);
            setDeleteCommentError(null);
          }
        }}
        title="Delete this internal note?"
      >
        <p className="text-sm text-neutral-700">
          The note and any attached files will be permanently removed.
          The deletion is recorded in the ticket's audit trail.
        </p>
        {deleteCommentError && (
          <Alert variant="error" className="mt-3">
            {deleteCommentError}
          </Alert>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setDeleteCommentId(null);
              setDeleteCommentError(null);
            }}
            disabled={deleteComment.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            className="bg-red-600 hover:bg-red-700 active:bg-red-800"
            onClick={() => {
              void handleConfirmDeleteComment();
            }}
            isLoading={deleteComment.isPending}
          >
            Delete Note
          </Button>
        </div>
      </Modal>

      {/* Remove-attachment modal. Triggered from any AttachmentTile's
          trash icon in any bubble or on the original-ticket entry.
          Hard-delete - the audit trail captures it via the backend's
          attachment_removed event. */}
      <Modal
        isOpen={removingAttachment !== null}
        onClose={() => {
          if (!deleteAttachment.isPending) closeRemoveAttachmentModal();
        }}
        title="Remove this attachment?"
      >
        <p className="text-sm text-neutral-700">
          {removingAttachment ? (
            <>
              <span className="font-medium">
                {removingAttachment.filename}
              </span>{" "}
              will be permanently removed from this ticket. The removal
              is recorded in the ticket's audit trail.
            </>
          ) : (
            "The attachment will be permanently removed."
          )}
        </p>
        {removeAttachmentError && (
          <Alert variant="error" className="mt-3">
            {removeAttachmentError}
          </Alert>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={closeRemoveAttachmentModal}
            disabled={deleteAttachment.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            className="bg-red-600 hover:bg-red-700 active:bg-red-800"
            onClick={() => {
              void handleConfirmRemoveAttachment();
            }}
            isLoading={deleteAttachment.isPending}
          >
            Remove
          </Button>
        </div>
      </Modal>

      {/* Delete modal (system_admin only) */}
      <Modal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete this ticket?"
      >
        <p className="text-sm text-neutral-700">
          The ticket will be soft-deleted. It stays in the audit trail
          but customers and other agents will no longer see it in any
          list view.
        </p>
        {deleteError && (
          <Alert variant="error" className="mt-3">
            {deleteError}
          </Alert>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setDeleteOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            className="bg-red-600 hover:bg-red-700 active:bg-red-800"
            onClick={() => {
              void handleConfirmDelete();
            }}
            isLoading={isDeleting}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default TicketDetailAgentPage;
